/*
 * Carbisdale - EditorInput
 *
 * Behandelt Keyboard- und Maus-Eingaben für den Editor.
 */

import { Position, PARA_TYPE } from './EditorModel.js'
import { SingleLineInput } from '../SingleLineInput.js'
import { LinkPicker } from './input/LinkPicker.js'
import { SeparatorInputBridge } from './input/SeparatorInputBridge.js'
import {
    getWordBounds,
    isWordChar,
    ensureSmartBlanks as tpEnsureSmartBlanks
} from '../TextProcessor.js'
import { computeStringLayout } from '../GlyphMetrics.js'
import { TIMING, EDITOR, TOUCH } from '../config.js'
import { NavigationHistory } from '../NavigationHistory.js'
import { isLinkTriggerPosition } from '../TextProcessor.js'
import {
    adjustSpansForDeleteRange, adjustSpansForInsert, adjustLinksForDeleteRange,
    adjustLinksForInsert, adjustSpansForDelete, adjustLinksForDelete,
    extendOrCreateSpan, getSpansInRange, getLinksInRange,
    shiftSpans, shiftLinks, mergeAdjacentSpans, markdownToRichText, markdownLinesToRichText, htmlToRichText
} from '../RichTextOps.js'

const _NAV_KEYS = new Set([
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End', 'PageUp', 'PageDown'
])

/**
 * EditorInput-Klasse
 * Verarbeitet Eingabe-Events und delegiert an Model/View.
 */
export class EditorInput {
    /**
     * @param {EditorModel} model - Das Datenmodell
     * @param {EditorView} view - Die View
     */
    constructor(model, view) {
        this.model = model
        this.view = view

        // Für Cleanup-on-Leave Detection
        this.lastParagraphId = null

        // Für Doppelklick-Detection
        this.lastClickTime = 0
        this.lastClickPos = null
        this.clickCount = 0
        this.DOUBLE_CLICK_THRESHOLD = TIMING.doubleClickThreshold

        // Für Drag-Selection (Main)
        this.isDragging = false
        this.dragStartPos = null
        this.hadDragMovement = false   // True wenn tatsächlich eine Mausbewegung während Drag stattfand
        this.justFinishedDrag = false  // Flag um Drag-Ende zu erkennen (click kommt NACH mouseup)

        // Für Note-Drag-Selection (Outline)
        this._isNoteDragging = false
        this._noteDragStartPos = null
        this._hadNoteDragMovement = false
        this.justFinishedNoteDrag = false

        // Für Info-Drag-Selection (Context)
        this._isInfoDragging = false
        this._infoDragStartPos = null
        this._hadInfoDragMovement = false
        this.justFinishedInfoDrag = false


        // Für Scrollbar-Drag
        this.isScrollbarDragging = false
        this.scrollbarDragStartY = 0
        this.scrollbarDragStartViewport = 0
        this.scrollbarViewHeight = null  // Externe Höhe wenn Scrollbar in anderem Pane

        // Goal Column für vertikale Navigation (getrennt pro Feld)
        this.goalColumn = null       // Für text-Feld (Main-Pane)
        this._noteGoalColumn = null  // Für note-Feld (Outline-Pane)
        this._infoGoalColumn = null  // Für info-Feld (Context-Pane)

        // Gespeicherte Offsets pro Feld (für Save/Restore beim Pane-Wechsel)
        this._lastMainOffset = 0
        this._lastNoteOffset = 0
        this._lastInfoOffset = 0

        // Callback für Cleanup (wird von ProseEditor gesetzt)
        this.onLeaveParagraph = null

        // Callback für blockierte Aktionen (Ping-Alarm)
        this.onActionBlocked = null

        // AutoCorrect-Engine (wird via setAutoCorrect() injiziert)
        this._autoCorrect = null

        // Format-Makro ON-Positionen: formatType → dataOffset (für OFF-Makro)
        this._formatMacroStart = new Map()

        // Für Wort-Snap-Selection (ab 3 Wörtern)
        this._wordSelectMode = false
        this._wordSelectAnchorWord = null  // { paragraphId, start, end }

        // Auto-Scroll bei Drag über Viewport-Grenzen
        this._autoScrollTimer = null       // setInterval ID
        this._autoScrollDirection = 0      // -1 = hoch, +1 = runter
        this._autoScrollSpeed = 0          // Abstand vom Rand in Zeilen (bestimmt Geschwindigkeit)
        this._lastMousePixelX = null       // Letzte pixelX für proportionale Position
        this._lastMouseX = 0              // Letzte screenX für mono Position

        // Internes Clipboard für Outline-Info und RT-Daten
        this._internalClipboard = null  // { text, outlineInfo, rtData?: [{formats, links}] }

        // Outline-Redesign Phase 3b: statt einen passiven Normal-Para zu mutieren,
        // setzt _activateTemporarily einen Pending-Cursor im Model. Beim ersten
        // Edit-Tastendruck materialisiert der Pending einen echten Outline-Item.
        // `_tempActivation` ist nur noch ein "wir haben gerade Pending gesetzt"-
        // Marker fuer die Filter-Mode-Cursor-Bookkeeping-Pfade. Auto-Cleanup
        // leerer Auto-Paras macht der zentrale Hook in _checkParagraphChange
        // via Model._autoCreatedParagraphIds — unabhaengig von _tempActivation.
        this._tempActivation = null  // { paraId }

        // Navigation-History für Section-Link-Sprünge (Zurück/Vorwärts)
        this._navigationHistory = new NavigationHistory()

        // Link-Picker — Kollaborateur-Klasse, die den Picker-State haelt.
        // EditorInput delegiert oeffentliche API weiter, damit externe
        // Callsites (main.js, overlay.js, mouseHandlers.js) unveraendert
        // bleiben.
        this._linkPicker = new LinkPicker(this.model, this.view, this)

        // DialogManager-Zugriff für Separator-Bestätigungsdialog (via DI)
        this._dialogManager = null

        // Callback: Ist Main-Pane sichtbar? (wird von main.js gesetzt)
        this.isMainVisible = () => true

        // Callback: Pane nach Link-Navigation aktivieren (wird von main.js gesetzt)
        // Bekommt das Ziel-Feld ('text', 'note', 'info') übergeben
        this.onActivatePane = null

        // Separator-Titel-Eingabe (SingleLineInput) + Bridge-Kollaborateur.
        // Die Bridge kapselt Activate/NavigateOut/Title-Change/Enter/Tab-
        // Callbacks — EditorInput delegiert an sie, behaelt aber Zugriff
        // auf _separatorInput und _separatorEditPane (transparent via
        // Accessor) fuer die vielen bestehenden Callsites.
        this._isSeparatorDragging = false
        this._separatorInput = new SingleLineInput({
            onChange: (text) => this._separatorBridge.onTitleChange(text),
            onEnter: () => this._separatorBridge.onEnter(),
            onTab: () => this._separatorBridge.onTab(),
            onShiftTab: () => this._separatorBridge.onShiftTab(),
            onNavigateOut: (dir) => this._separatorBridge.onNavigateOut(dir)
        })
        this._separatorBridge = new SeparatorInputBridge(this, this._separatorInput)

    }

    /** Pane-Quelle der Separator-Bearbeitung ('main' | 'outline' | 'context'). */
    get _separatorEditPane() { return this._separatorBridge.editPane }
    set _separatorEditPane(value) { this._separatorBridge.editPane = value }

    /**
     * Setzt transienten State zurück (bei Projektwechsel).
     * Model-State (Undo, Cursor) wird von setParagraphsRT() behandelt,
     * hier nur EditorInput-eigener State.
     */
    resetTransientState() {
        this.lastParagraphId = null
        this._formatMacroStart.clear()
        this._wordSelectMode = false
        this._wordSelectAnchorWord = null
        this._tempActivation = null
        this._navigationHistory = new NavigationHistory()
        this._internalClipboard = null
        this._linkPicker.reset()
        // Auto-Scroll stoppen falls aktiv
        if (this._autoScrollTimer) {
            clearInterval(this._autoScrollTimer)
            this._autoScrollTimer = null
        }
        this._autoScrollDirection = 0
        this._autoScrollSpeed = 0
    }

    /**
     * Setzt die AutoCorrect-Engine (Dependency Injection).
     * @param {AutoCorrect} autoCorrect
     */
    setAutoCorrect(autoCorrect) {
        this._autoCorrect = autoCorrect
    }

    /**
     * Setzt den DialogManager für Separator-Bestätigungsdialoge (DI).
     * @param {DialogManager} dialogManager
     */
    setDialogManager(dialogManager) {
        this._dialogManager = dialogManager
    }

    /**
     * Setzt die Goal Columns zurück (bei horizontaler Navigation, Texteingabe, Klick).
     */
    _resetGoalColumn() {
        this.goalColumn = null
        this._noteGoalColumn = null
        this._infoGoalColumn = null
    }

    /**
     * RT-Modus: Wenn der Cursor auf einem \uFFFC-Link steht, zum Ende springen und highlighten.
     * Wird nach vertikaler Navigation (Up/Down) und Home aufgerufen.
     */
    _snapOverRTLink() {
        if (!this.model.cursor) return
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return
        const field = this.model.cursor.field || 'text'
        const text = this.model._getFieldText(para, field)
        if (!text || text[this.model.cursor.offset] !== '\uFFFC') return

        // Link-Entry an dieser Position finden
        const links = para.links[field]
        if (!links) return
        const link = links.find(l => l.offset === this.model.cursor.offset)
        if (!link) return

        // Cursor ans Ende des Links setzen
        this.model.cursor.offset = link.offset + 1
        // Link als selektiert markieren
        this.model._selectedLink = {
            paragraphId: para.id, field,
            dataStart: link.offset, dataEnd: link.offset + 1,
            targetId: link.targetId
        }
        this.model._markParagraphDirty(para.id)
        this.view.markDirty()
    }

    /**
     * Springt über Link-Marker wenn der Cursor innerhalb eines [[@id]] steht.
     * Hinweis: Prüft nur Cursor INNERHALB des Markers (nicht an dataEnd).
     * Für Navigation von dataEnd aus → siehe ArrowLeft-Handler.
     * @param {string} direction - 'right' oder 'left'
     */
    _skipOverLinks(direction) {
        if (!this.model.cursor) return
        const link = this.model.getLinkAtCursor()
        if (!link) return
        if (direction === 'right') {
            this.model.cursor.offset = link.dataEnd
        } else {
            this.model.cursor.offset = link.dataStart
        }
    }

    /**
     * Findet einen Link direkt VOR dem Cursor (dataEnd === cursor.offset).
     * @returns {object|null} {paragraphId, field, dataStart, dataEnd, targetId} oder null
     */
    _getLinkBeforeCursor() {
        if (!this.model.cursor) return null
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return null
        const field = this.model.cursor.field || 'text'

        // RT: \uFFFC ist 1 Zeichen, Link "vor" dem Cursor = offset - 1
        const links = para.links[field]
        if (!links || links.length === 0) return null
        const offset = this.model.cursor.offset
        for (const link of links) {
            if (link.offset + 1 === offset) {
                return { paragraphId: para.id, field, dataStart: link.offset, dataEnd: link.offset + 1, targetId: link.targetId }
            }
        }
        return null
    }

    /**
     * Findet einen Link direkt NACH dem Cursor (dataStart === cursor.offset).
     * @returns {object|null} {paragraphId, field, dataStart, dataEnd, targetId} oder null
     */
    _getLinkAfterCursor() {
        if (!this.model.cursor) return null
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return null
        const field = this.model.cursor.field || 'text'

        // RT: \uFFFC ist 1 Zeichen, Link "nach" dem Cursor = offset
        const links = para.links[field]
        if (!links || links.length === 0) return null
        const offset = this.model.cursor.offset
        for (const link of links) {
            if (link.offset === offset) {
                return { paragraphId: para.id, field, dataStart: link.offset, dataEnd: link.offset + 1, targetId: link.targetId }
            }
        }
        return null
    }

    /**
     * Backspace auf einem Link: Erster Druck selektiert, zweiter löscht.
     * @returns {boolean} true wenn Link-Handling aktiv war
     */
    _handleLinkBackspace() {
        const link = this._getLinkBeforeCursor()
        if (!link) {
            // Kein Link am Cursor → _selectedLink zurücksetzen
            if (this.model._selectedLink) {
                this.model._selectedLink = null
                this.view.markDirty()
            }
            return false
        }
        // Link bereits selektiert → löschen
        if (this.model._selectedLink &&
            this.model._selectedLink.paragraphId === link.paragraphId &&
            this.model._selectedLink.field === link.field &&
            this.model._selectedLink.dataStart === link.dataStart) {
            this.model.deleteLink(link.paragraphId, link.field, link.dataStart, link.dataEnd)
            this.model._selectedLink = null
            this.view.markDirty()
            this.view.ensureCursorVisible()
            return true
        }
        // Erster Druck → Link selektieren (visuelles Highlighting)
        this.model._selectedLink = link
        this.model._markParagraphDirty(link.paragraphId)
        this.view.markDirty()
        return true
    }

    /**
     * Delete auf einem Link: Erster Druck selektiert, zweiter löscht.
     * @returns {boolean} true wenn Link-Handling aktiv war
     */
    _handleLinkDelete() {
        const link = this._getLinkAfterCursor()
        if (!link) {
            // Kein Link am Cursor → _selectedLink zurücksetzen
            if (this.model._selectedLink) {
                this.model._selectedLink = null
                this.view.markDirty()
            }
            return false
        }
        // Link bereits selektiert → löschen
        if (this.model._selectedLink &&
            this.model._selectedLink.paragraphId === link.paragraphId &&
            this.model._selectedLink.field === link.field &&
            this.model._selectedLink.dataStart === link.dataStart) {
            this.model.deleteLink(link.paragraphId, link.field, link.dataStart, link.dataEnd)
            this.model._selectedLink = null
            this.view.markDirty()
            this.view.ensureCursorVisible()
            return true
        }
        // Erster Druck → Link selektieren (visuelles Highlighting)
        this.model._selectedLink = link
        this.model._markParagraphDirty(link.paragraphId)
        this.view.markDirty()
        return true
    }

    /**
     * Setzt _selectedLink wenn der Cursor nach Navigation an einer Link-Grenze steht.
     * Wird nur bei einfachen Pfeiltasten (ohne Shift/Cmd) aufgerufen.
     * @param {string} direction - 'right' oder 'left'
     */
    _markNavigatedLink(direction) {
        const link = direction === 'right'
            ? this._getLinkBeforeCursor()
            : this._getLinkAfterCursor()
        if (link) {
            this.model._selectedLink = link
            // PARAGRAPH-Level erzwingen, damit der Cursor-Only-Pfad umgangen wird
            // und das Link-Highlighting tatsächlich gerendert wird
            this.model._markParagraphDirty(link.paragraphId)
            this.view.markDirty()
        }
    }

    /**
     * Passt Selection-Endpunkte an, damit sie nicht auf Marker-Positionen liegen.
     * @param {string} direction - 'right' oder 'left'
     */
    _skipSelectionOverMarkers(direction = 'right') {
        if (!this.model.selection) return
        this._snapOverRTLink()
        // Auch selection.cursor synchronisieren
        if (this.model.selection) {
            this.model.selection.cursor = this.model.cursor.clone()
        }
    }

    /**
     * Wechselt das Cursor-Feld (text ↔ note ↔ info).
     * Speichert den aktuellen Offset und restauriert den gespeicherten Offset des Ziel-Feldes.
     * @param {string} newField - 'text', 'note' oder 'info'
     */
    switchField(newField) {
        if (!this.model.cursor) return
        if (this.model.cursor.field === newField) return

        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return

        // Bei Wechsel von Note → anderes Feld: temporäre Aktivierung prüfen
        if (this.model.cursor.field === 'note' && newField !== 'note') {
            this._checkTempActivationRevert()
        }

        // Aktuellen Offset für das verlassene Feld speichern
        const currentField = this.model.cursor.field
        if (currentField === 'text') {
            this._lastMainOffset = this.model.cursor.offset
        } else if (currentField === 'note') {
            this._lastNoteOffset = this.model.cursor.offset
        } else if (currentField === 'info') {
            this._lastInfoOffset = this.model.cursor.offset
        }

        this.model.cursor.field = newField

        // Gespeicherten Offset restaurieren (geclampt auf Feldlänge)
        const fieldText = this.model._getFieldText(para, newField)
        const savedOffset = newField === 'text' ? this._lastMainOffset
            : newField === 'note' ? this._lastNoteOffset : this._lastInfoOffset
        this.model.cursor.offset = Math.min(savedOffset, fieldText.length)

        // Selection aufheben beim Feld-Wechsel
        this.model.selection = null

        this._resetGoalColumn()
        // Kein ensureCursorVisible() hier — Aufrufer entscheiden selbst
        // (Tab-Wechsel: ja, Klick-Wechsel: nein, weil Klick eigenen Cursor setzt)
    }

    // ===== Keyboard Handling =====

    /**
     * Behandelt ein Keydown-Event.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleKeydown(e) {
        // Platform-unabhängig: Cmd (Mac) ODER Ctrl (Windows/Linux)
        const cmdKey = e.metaKey || e.ctrlKey

        // Zoom-Shortcuts (Cmd+/Cmd-/Cmd+0) immer durchlassen
        if (cmdKey && !e.altKey && !e.shiftKey && (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '0')) {
            return false
        }

        // Separator-Titel-Eingabe: alle Events an SingleLineInput delegieren
        if (this._isOnSeparator()) {
            // Cmd+Enter: Section-Collapse togglen (auch auf Background-Separator).
            // VOR allem anderen abfangen, sonst schluckt SingleLineInput das Event.
            if (cmdKey && e.key === 'Enter' && !e.shiftKey && !e.altKey) {
                this._handleCollapseToggle()
                return true
            }
            // Background-Separator: nur Navigation durchlassen, keine Eingabe
            const para = this.model.getParagraph(this.model.cursor.paragraphId)
            if (para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                // Navigation und Undo/Redo durchlassen
                if (this._handleNavigation(e)) {
                    this._checkParagraphChange()
                    return true
                }
                if (cmdKey && (e.key === 'z' || e.key === 'Z')) {
                    if (e.shiftKey) this.model.redo()
                    else this.model.undo()
                    this.view.ensureCursorVisible()
                    return true
                }
                // Escape durchlassen (Pane-Fullscreen-Toggle)
                if (e.key === 'Escape') return false
                return true // Alle anderen Tasten blockieren
            }
            // Undo/Redo durchlassen
            if (cmdKey && (e.key === 'z' || e.key === 'Z')) {
                if (e.shiftKey) this.model.redo()
                else this.model.undo()
                // Nach Undo: Separator-Input synchronisieren falls noch auf Separator
                if (this._isOnSeparator()) this._activateSeparatorInput()
                this.view.ensureCursorVisible()
                return true
            }
            return this._separatorInput.handleKeydown(e)
        }

        // Link-Picker: alle Tasten abfangen wenn aktiv
        if (this._linkPicker.isActive) {
            return this._linkPicker.handleKey(e)
        }

        // Outline-Selection: spezielle Tasten abfangen
        if (this.model.hasOutlineSelection()) {
            if (this._handleOutlineSelectionKey(e)) return true
            // false = Selection aufgehoben, Taste soll normal verarbeitet werden → weiter
        }

        // _selectedLink bei allen Tasten außer Backspace/Delete zurücksetzen
        if (this.model._selectedLink && e.key !== 'Backspace' && e.key !== 'Delete') {
            this.model._markParagraphDirty(this.model._selectedLink.paragraphId)
            this.model._selectedLink = null
            this.view.markDirty()
        }

        // AutoCorrect: Pending-Replacement sichern und bei jeder Taste konsumieren
        const pendingAutoCorrect = this._autoCorrect ? this._autoCorrect.lastReplacement : null
        if (this._autoCorrect) this._autoCorrect.clearLastReplacement()

        // Navigation
        if (this._handleNavigation(e)) {
            this._checkParagraphChange()
            return true
        }

        // Collapsed-Active text: Text-Modifikationen grundsätzlich gesperrt.
        // Erlaubt bleiben: Cmd+Enter (Unfold), Cmd+Z/Y (Undo/Redo) — diese werden
        // später in ihren Zweigen geprüft. Alles andere → Ping.
        if (this._isCollapsedActiveTextAtCursor()) {
            // Cmd+Enter: Unfold-Toggle
            if (cmdKey && e.key === 'Enter' && !e.shiftKey && !e.altKey) {
                this._handleCollapseToggle()
                return true
            }
            // Cmd+Z / Cmd+Shift+Z: Undo/Redo durchlassen
            if (cmdKey && (e.key === 'z' || e.key === 'Z')) {
                if (e.shiftKey) this.model.redo()
                else this.model.undo()
                this.view.ensureCursorVisible()
                return true
            }
            // Escape, F-Keys etc. durchlassen
            if (e.key === 'Escape' || e.key.startsWith('F')) return false
            // Alles andere blockieren
            this._pingBlocked()
            return true
        }

        // Format-Shortcuts (Cmd+B, Cmd+I, Cmd+U, Cmd+E, Cmd+Shift+X)
        if (cmdKey && !e.altKey) {
            if (e.key === 'b' || e.key === 'B') {
                if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
                this.model.toggleBold()
                if (this.model._actionBlocked) this._pingBlocked()
                return true
            }
            if (e.key === 'i' || e.key === 'I') {
                if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
                this.model.toggleItalic()
                if (this.model._actionBlocked) this._pingBlocked()
                return true
            }
            if (e.key === 'u' || e.key === 'U') {
                if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
                this.model.toggleUnderline()
                if (this.model._actionBlocked) this._pingBlocked()
                return true
            }
            if ((e.key === 'e' || e.key === 'E') && !e.shiftKey) {
                if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
                this.model.toggleCode()
                if (this.model._actionBlocked) this._pingBlocked()
                return true
            }
            if ((e.key === 'x' || e.key === 'X') && e.shiftKey) {
                if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
                this.model.toggleStrikethrough()
                if (this.model._actionBlocked) this._pingBlocked()
                return true
            }

            // Undo/Redo
            if (e.key === 'z' || e.key === 'Z') {
                if (e.shiftKey) {
                    this.model.redo()
                } else {
                    this.model.undo()
                }
                this.view.ensureCursorVisible()
                return true
            }

            // Select All
            if (e.key === 'a' || e.key === 'A') {
                this.model.selectAll()
                return true
            }
        }

        // Backspace
        if (e.key === 'Backspace') {
            if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
            // Link-Behandlung: \uFFFC ist ein normales Zeichen, aber erst selektieren dann löschen
            if (!this.model.hasSelection()) {
                const linkResult = this._handleLinkBackspace()
                if (linkResult) return true
            }
            // AutoCorrect/Format-Makro-Undo: Backspace direkt nach Replacement → rückgängig
            if (pendingAutoCorrect && !this.model.hasSelection()) {
                const acPara = this.model.getParagraph(this.model.cursor.paragraphId)
                const acField = this.model.cursor.field || 'text'
                if (acPara && pendingAutoCorrect.paragraphId === acPara.id &&
                    pendingAutoCorrect.field === acField &&
                    this.model.cursor.offset === pendingAutoCorrect.offset) {
                    this.model.undo()
                    // Format-Makro ON-Position zurücksetzen
                    if (pendingAutoCorrect.kind === 'formatOn') {
                        this._formatMacroStart.delete(pendingAutoCorrect.formatType)
                    }
                    this.view.ensureCursorVisible()
                    return true
                }
            }
            // Block-Format entfernen: Backspace an Position 0 bei formatiertem Absatz.
            // Nur im text-Feld — sonst loescht ein Backspace am Anfang eines leeren
            // note/info-Feldes faelschlich das Block-Format des text-Paragraphs
            // (die Format-Info gehoert zum Absatz als Ganzem, nicht zum note-Feld).
            const bsField = this.model.cursor.field || 'text'
            if (!this.model.hasSelection() && this.model.cursor.offset === 0 && bsField === 'text') {
                const bsPara = this.model.getParagraph(this.model.cursor.paragraphId)
                if (bsPara && (bsPara.listType || bsPara.headingLevel > 0 || bsPara.blockquote)) {
                    // Erster Backspace: nur Format entfernen, kein Merge
                    const resetChanges = {}
                    if (bsPara.listType) {
                        // Listen: stufenweise dedenten (wie bei leerem Enter)
                        if (bsPara.listLevel > 0) {
                            resetChanges.listLevel = bsPara.listLevel - 1
                        } else {
                            resetChanges.listType = null
                            resetChanges.listLevel = 0
                        }
                    } else if (bsPara.headingLevel > 0) {
                        resetChanges.headingLevel = 0
                    } else if (bsPara.blockquote) {
                        resetChanges.blockquote = false
                    }
                    this.model.setBlockFormat(bsPara.id, resetChanges)
                    this.view.ensureCursorVisible()
                    return true
                }
            }
            this.model.deleteCharBefore()
            if (this.model._separatorBlocked) {
                this._handleSeparatorRemoveBackspace()
            }
            if (this.model._actionBlocked) this._pingBlocked()
            this.model._updateActiveFormats()
            this._checkParagraphChange()
            this.view.ensureCursorVisible()
            return true
        }

        // Delete
        if (e.key === 'Delete') {
            if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
            if (!this.model.hasSelection()) {
                const linkResult = this._handleLinkDelete()
                if (linkResult) return true
            }
            this.model.deleteCharAfter()
            if (this.model._separatorBlocked) {
                this._handleSeparatorRemoveDelete()
            }
            if (this.model._actionBlocked) this._pingBlocked()
            this.model._updateActiveFormats()
            this._checkParagraphChange()
            this.view.ensureCursorVisible()
            return true
        }

        // Enter
        if (e.key === 'Enter') {
            // Alt+Enter: Section Lock toggeln
            if (e.altKey && !cmdKey && !e.shiftKey) {
                this.model.toggleSectionLock(this.model.cursor.paragraphId)
                this.view.markDirty()
                return true
            }
            // Cmd+Enter: Collapse toggeln (Section bei Separator, Active bei aktivem Absatz)
            if (cmdKey && !e.shiftKey && !e.altKey) {
                this._handleCollapseToggle()
                return true
            }
            if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
            const hasSelection = this.model.hasSelection()
            const snapshot = hasSelection ? this._takeSnapshot() : null
            const undoSize = hasSelection ? this.model.undoStack.length : 0

            if (e.shiftKey) {
                this._handleShiftEnter()
            } else {
                this._handleEnter()
            }

            // Bei Selection: mehrere Undo-Einträge zu einem zusammenfassen
            if (hasSelection && this.model.undoStack.length - undoSize > 1) {
                this._collapseUndoEntries(undoSize, snapshot)
            }

            // Enter/Shift+Enter bricht Konsolidierung
            this.model._isInConsolidationGroup = false
            this.model.charsSinceUndoPoint = 0

            this._checkParagraphChange()
            this.view.ensureCursorVisible()
            return true
        }

        // Tab (Listen-Einrückung oder Outline-Tiefe) — Alt+Tab ist Pane-Navigation (main.js)
        if (e.key === 'Tab' && !e.altKey) {
            // Info-Feld: Tab/Shift-Tab tut nichts
            if (this.model.cursor && this.model.cursor.field === 'info') {
                return false
            }
            if (this.model.cursor && this.model.cursor.field === 'note') {
                const handled = this._handleNoteTab(e.shiftKey)
                if (handled) this.view.ensureCursorVisible()
                return handled
            }
            if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
            const handled = this._handleTextTab(e.shiftKey)
            if (handled) this.view.ensureCursorVisible()
            return handled
        }

        // Navigation-History (Alt+Pfeiltasten)
        if (e.altKey && !cmdKey && !e.shiftKey) {
            if (e.key === 'ArrowLeft') {
                this._navigateBack()
                return true
            }
            if (e.key === 'ArrowRight') {
                this._navigateForward()
                return true
            }
        }

        // Printable character
        const charKey = this._charForKey(e)
        if (charKey !== null) {
            if (this._isEditBlockedAtCursor()) { this._pingBlocked(); return true }
            // Outline-Redesign Phase 3b: Pending-Cursor materialisieren, bevor
            // das Zeichen verarbeitet wird. Cursor sitzt danach auf der
            // note-Position des neuen Outline-Item-Paras. Auto-Cleanup macht
            // der zentrale Hook in _checkParagraphChange (Spec §4.4: leerer
            // Auto-Para wird beim Verlassen geloescht, auch wenn er
            // zwischendurch Inhalt hatte).
            this._materializePendingForEdit()

            // Atomarer Undo-Schutz bei Selection (deleteSelection + insertText = 1 Undo)
            const hasSelection = this.model.hasSelection()
            const snapshot = hasSelection ? this._takeSnapshot() : null
            const undoSize = hasSelection ? this.model.undoStack.length : 0

            // hr → normal bei Texteingabe
            const hrPara = this.model.getParagraph(this.model.cursor.paragraphId)
            if (hrPara && hrPara.type === 'hr') {
                this.model.setBlockFormat(hrPara.id, { type: 'normal' })
                const mutHr = this.model._mutate(this.model.getParagraph(hrPara.id))
                if (mutHr) {
                    mutHr.text = '---'
                    this.model.cursor.offset = 3
                    this.model._markParagraphDirty(mutHr.id)
                }
            }
            // Rich-Text: kein Smart Insert, kein Marker-Skip
            this.model.insertText(charKey)

            // Bei Selection: mehrere Undo-Einträge zu einem zusammenfassen
            if (hasSelection && this.model.undoStack.length - undoSize > 1) {
                this._collapseUndoEntries(undoSize, snapshot)
            }

            // AutoCorrect: Trigger prüfen und ersetzen.
            // Code-Block-Paragraphs sind raw — keine Inline-Format-Macros,
            // keine Emoji-Ersetzung. User soll Code unverfälscht tippen können.
            if (this._autoCorrect) {
                const para = this.model.getParagraph(this.model.cursor.paragraphId)
                const field = this.model.cursor.field || 'text'
                const isCodeText = para && field === 'text' && !!para.codeBlock
                if (para && !isCodeText) {
                    const fieldText = this.model._getFieldText(para, field)
                    // RT-Modus: Format-Makro-Optionen übergeben
                    const acOptions = { activeFormats: this.model._activeFormats }
                    const match = this._autoCorrect.check(fieldText, this.model.cursor.offset, acOptions)
                    if (match) {
                        if (match.kind === 'formatOn') {
                            this._applyFormatMacroOn(para, field, fieldText, match)
                        } else if (match.kind === 'formatOff') {
                            this._applyFormatMacroOff(para, field, fieldText, match)
                        } else {
                            // Emoji-Replacement (kind === 'emoji' oder legacy ohne kind)
                            this._applyEmojiReplacement(para, field, fieldText, match)
                        }
                    }
                }
            }

            // Block-Format-Makros (- , 1. , # , > , ---)
            if ((this.model.cursor.field || 'text') === 'text') {
                this._checkBlockFormatMacro()
            }

            // Link-Trigger: '@' am Wortanfang → Link-Picker öffnen
            if (e.key === '@' && !this.model.isSeparator(this.model.cursor.paragraphId)) {
                const curPara = this.model.getParagraph(this.model.cursor.paragraphId)
                if (curPara) {
                    const curField = this.model.cursor.field || 'text'
                    const curText = this.model._getFieldText(curPara, curField)
                    // Position VOR dem eingefügten @
                    const atPos = this.model.cursor.offset - 1
                    if (atPos >= 0 && isLinkTriggerPosition(curText, atPos)) {
                        this._linkPicker.open()
                    }
                }
            }

            this._resetGoalColumn()
            this._checkParagraphChange()
            this.view.ensureCursorVisible()
            return true
        }

        return false
    }

    /**
     * Behandelt Navigations-Tasten.
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    _handleNavigation(e) {
        // Platform-unabhängig: Cmd (Mac) ODER Ctrl (Windows/Linux)
        const cmdKey = e.metaKey || e.ctrlKey

        // Bei Shift: Selection erweitern
        if (e.shiftKey && !this.model.selection && this._isNavigationKey(e.key)) {
            this.model.startSelection()
        }

        let handled = false

        switch (e.key) {
            case 'ArrowLeft': {
                // Alt+Left ist für Navigation-History reserviert
                if (e.altKey && !cmdKey && !e.shiftKey) break
                // Pre-Move-Feld fuer Separator-Pane-Wahl merken (das Move kann
                // cursor.field auf 'text' umstellen, wenn ein Separator getroffen wird)
                const preMoveFieldL = this.model.cursor?.field || 'text'
                // Rich-Text: keine Marker, kein Prefix
                if (cmdKey) {
                    this.model.moveCursorToLineStart()
                } else if (preMoveFieldL === 'info' && !e.shiftKey) {
                    // Context-Pane: filter-bewusster Paragraph-Uebergang
                    this._moveCursorHorizontalInfo('left')
                } else if (preMoveFieldL === 'note' && !e.shiftKey
                        && this.view._paneFilterMode !== null) {
                    // Outline-Pane filter-bewusst: springt zum naechsten aktiven
                    // Absatz bzw. Separator-Label (Main sichtbar bleibt beim alten
                    // moveCursor-Pfad, weil dort passive Absaetze auch ansteuerbar sind).
                    this._moveCursorHorizontalNote('left')
                } else {
                    this.model.moveCursor('left')
                }
                // Collapsed-Bereich rückwärts: an Pos 0 eines Folge-Absatzes würde
                // moveCursor in den versteckten Range wandern — stattdessen auf das
                // sichtbare Ende des Hosts (vor "…") zurückziehen.
                if (!cmdKey && !e.shiftKey && preMoveFieldL === 'text') {
                    this._clampCursorOffHidden()
                }
                // Auf Separator gelandet: Label-Eingabe aktivieren (Cursor ans Ende)
                if (!e.shiftKey && this._isOnSeparator()) {
                    this.model.clearSelection()
                    const para = this.model.getParagraph(this.model.cursor.paragraphId)
                    const pane = this._separatorEditPaneForField(preMoveFieldL)
                    this._activateSeparatorInput(para ? para.text.length : 0, pane)
                    this._checkParagraphChange()
                    this.view.ensureCursorVisible()
                    return true
                }
                if (!e.shiftKey && !cmdKey) {
                    this._markNavigatedLink('left')
                }
                this.model._updateActiveFormats()
                this._resetGoalColumn()
                handled = true
                break
            }

            case 'ArrowRight': {
                // Alt+Right ist für Navigation-History reserviert
                if (e.altKey && !cmdKey && !e.shiftKey) break
                // Pre-Move-Feld fuer Separator-Pane-Wahl merken
                const preMoveFieldR = this.model.cursor?.field || 'text'
                // Collapsed-Host: Cursor darf nicht über das letzte sichtbare Zeichen
                // (vor "…") hinaus — sonst landet er im versteckten Text. Ping.
                if (!cmdKey && preMoveFieldR === 'text') {
                    const visEndR = this._collapsedHostVisibleEnd()
                    if (visEndR !== null && (this.model.cursor.offset || 0) >= visEndR) {
                        this._pingBlocked()
                        handled = true
                        break
                    }
                }
                // Rich-Text: keine Marker, kein Prefix
                if (cmdKey) {
                    this.model.moveCursorToLineEnd()
                } else if (preMoveFieldR === 'info' && !e.shiftKey) {
                    // Context-Pane: filter-bewusster Paragraph-Uebergang
                    this._moveCursorHorizontalInfo('right')
                } else if (preMoveFieldR === 'note' && !e.shiftKey
                        && this.view._paneFilterMode !== null) {
                    // Outline-Pane filter-bewusst (siehe ArrowLeft-Kommentar)
                    this._moveCursorHorizontalNote('right')
                } else {
                    this.model.moveCursor('right')
                }
                // Auf Separator gelandet: Label-Eingabe aktivieren (Cursor an Pos 0)
                if (!e.shiftKey && this._isOnSeparator()) {
                    this.model.clearSelection()
                    const pane = this._separatorEditPaneForField(preMoveFieldR)
                    this._activateSeparatorInput(0, pane)
                    this._checkParagraphChange()
                    this.view.ensureCursorVisible()
                    return true
                }
                if (!e.shiftKey && !cmdKey) {
                    this._markNavigatedLink('right')
                }
                this.model._updateActiveFormats()
                this._resetGoalColumn()
                handled = true
                break
            }

            case 'ArrowUp': {
                // Alt+Up: Absatz/Subtree/Section nach oben verschieben
                if (e.altKey && !cmdKey && !e.shiftKey) {
                    if (this._isLockedAtCursor()) { this._pingBlocked(); handled = true; break }
                    const field = this.model.cursor?.field || 'text'
                    const moved = this.model.moveAtCursor(this.model.cursor.paragraphId, field, 'up')
                    if (moved) this.view.ensureCursorVisible()
                    else this._pingBlocked()
                    handled = true
                    break
                }
                if (cmdKey) {
                    // Cmd+Up: zum Abschnittsanfang (statt Dokumentanfang)
                    const { startIndex: upStart } = this.model.getSectionBounds(this.model.cursor.paragraphId)
                    const upFirstPara = this.model.paragraphs[upStart]
                    this.model.cursor = new Position(upFirstPara.id, 0, this.model.cursor.field)
                    this.model._markCursorDirty()
                    this._resetGoalColumn()
                } else {
                    this._moveVertical(-1)
                }
                this._snapOverRTLink()
                handled = true
                break
            }

            case 'ArrowDown': {
                // Alt+Down: Absatz/Subtree/Section nach unten verschieben
                if (e.altKey && !cmdKey && !e.shiftKey) {
                    if (this._isLockedAtCursor()) { this._pingBlocked(); handled = true; break }
                    const field = this.model.cursor?.field || 'text'
                    const moved = this.model.moveAtCursor(this.model.cursor.paragraphId, field, 'down')
                    if (moved) this.view.ensureCursorVisible()
                    else this._pingBlocked()
                    handled = true
                    break
                }
                if (cmdKey) {
                    // Cmd+Down: zum Abschnittsende (statt Dokumentende)
                    const { endIndex: downEnd } = this.model.getSectionBounds(this.model.cursor.paragraphId)
                    const downLastPara = this.model.paragraphs[downEnd]
                    const downFieldText = this.model._getFieldText(downLastPara, this.model.cursor.field)
                    this.model.cursor = new Position(downLastPara.id, downFieldText.length, this.model.cursor.field)
                    this.model._markCursorDirty()
                    this._resetGoalColumn()
                } else {
                    this._moveVertical(1)
                }
                this._snapOverRTLink()
                handled = true
                break
            }

            case 'Home': {
                if (cmdKey) {
                    this.model.moveCursorToDocStart()
                } else {
                    this.model.moveCursorToLineStart()
                }
                this._snapOverRTLink()
                this._resetGoalColumn()
                handled = true
                break
            }

            case 'End':
                if (cmdKey) {
                    this.model.moveCursorToDocEnd()
                } else {
                    this.model.moveCursorToLineEnd()
                }
                this._snapOverRTLink()
                this._resetGoalColumn()
                handled = true
                break

            case 'PageUp':
                this._handlePageUp()
                this._resetGoalColumn()
                handled = true
                break

            case 'PageDown':
                this._handlePageDown()
                this._resetGoalColumn()
                handled = true
                break
        }

        // Selection erweitern oder löschen
        if (handled) {
            if (e.shiftKey) {
                this._skipSelectionOverMarkers(
                    e.key === 'ArrowLeft' || e.key === 'Home' ? 'left' : 'right'
                )
                this.model.extendSelection()
                this._clampSelectionToSection()
            } else if (this.model.selection && this._isNavigationKey(e.key)) {
                this.model.clearSelection()
            }
            // Cursor sichtbar halten bei Navigation
            this.view.ensureCursorVisible()
        }

        return handled
    }

    /**
     * Prüft ob eine Taste eine Navigations-Taste ist.
     * @param {string} key
     * @returns {boolean}
     */
    _isNavigationKey(key) {
        return _NAV_KEYS.has(key)
    }

    /**
     * Behandelt PageUp.
     */
    _handlePageUp() {
        const pageSize = this.view.height - 1
        // Cursor um eine Seite nach oben
        for (let i = 0; i < pageSize; i++) {
            if (!this.model.moveCursor('up')) break
        }
        this.view.scrollBy(-pageSize)
    }

    /**
     * Behandelt PageDown.
     */
    _handlePageDown() {
        const pageSize = this.view.height - 1
        // Cursor um eine Seite nach unten
        for (let i = 0; i < pageSize; i++) {
            if (!this.model.moveCursor('down')) break
        }
        this.view.scrollBy(pageSize)
    }

    /**
     * Bewegt den Cursor vertikal um eine Screen-Zeile.
     * Nutzt feld-spezifische Zeilen-Arrays und Goal Columns.
     *
     * @param {number} direction - -1 für hoch, 1 für runter
     */
    _moveVertical(direction) {
        if (!this.model.cursor) return

        const field = this.model.cursor.field || 'text'
        const isNote = field === 'note'
        const isInfo = field === 'info'
        const isProp = this.view.renderer && this.view.renderer.isProportional()

        // Feld-spezifische Zeilen und Positionsmapping verwenden
        const lines = isInfo ? this.view.infoLines
            : isNote ? this.view.noteLines : this.view.lines
        const currentPos = isInfo
            ? this.view.infoDataToScreen(this.model.cursor.paragraphId, this.model.cursor.offset)
            : isNote
            ? this.view.noteDataToScreen(this.model.cursor.paragraphId, this.model.cursor.offset)
            : this.view.dataToScreen(this.model.cursor.paragraphId, this.model.cursor.offset)
        if (!currentPos) return

        const currentLineIndex = isInfo
            ? this.view.getInfoLineIndexForPosition(this.model.cursor.paragraphId, this.model.cursor.offset)
            : isNote
            ? this.view.getNoteLineIndexForPosition(this.model.cursor.paragraphId, this.model.cursor.offset)
            : this.view.getLineIndexForPosition(this.model.cursor.paragraphId, this.model.cursor.offset)
        if (currentLineIndex < 0) return

        // Goal Column setzen wenn noch nicht gesetzt (getrennt pro Feld)
        // Proportional: Pixel-Position speichern, Monospace: Spalten-Nummer
        if (isInfo) {
            if (this._infoGoalColumn === null) {
                this._infoGoalColumn = (isProp && currentPos.pixelX !== undefined) ? currentPos.pixelX : currentPos.x
            }
        } else if (isNote) {
            if (this._noteGoalColumn === null) {
                this._noteGoalColumn = (isProp && currentPos.pixelX !== undefined) ? currentPos.pixelX : currentPos.x
            }
        } else {
            if (this.goalColumn === null) {
                this.goalColumn = (isProp && currentPos.pixelX !== undefined) ? currentPos.pixelX : currentPos.x
            }
        }
        const goalCol = isInfo ? this._infoGoalColumn
            : isNote ? this._noteGoalColumn : this.goalColumn

        // Ziel-Zeile: nur auf sichtbare Absaetze oder Separator-Labels stoppen
        // (_isArrowStopInField kennt die Filter-Regeln des Panes). Sync-Padding
        // wird uebersprungen, provisorisch-leere Absaetze (Feld == '') sind
        // gueltige Stopps — Cursor landet zwischen den >< Markern.
        let targetLineIndex = currentLineIndex + direction
        while (targetLineIndex >= 0 && targetLineIndex < lines.length &&
               !this.view._isArrowStopInField(targetLineIndex, field)) {
            targetLineIndex += direction
        }

        // Im Note-Feld: Passive Absätze überspringen (nur aktive haben Notes).
        // Info-Feld: KEIN Überspringen (jeder Absatz kann ein Info haben).
        //
        // Im Filter-Mode (Main hidden) sind provisorische Passive sichtbar und
        // sollen per Pfeil ansteuerbar sein — sie werden beim Landen temporaer
        // aktiviert (siehe unten im Cursor-Update-Block). Separator-Label
        // bleiben ebenfalls Stopps (sind in der Outline sichtbar).
        // Das Filtern der Zeilen hat _isArrowStopInField oben bereits erledigt;
        // wir muessen hier nichts mehr skippen.
        //
        // Im Normalmodus bleibt das bisherige Verhalten: passive Absaetze werden
        // ueberflogen bis zum naechsten aktiven; Separatoren werden dabei mit
        // uebersprungen (Main-Pane zeigt den Label — die Navigation zieht lieber
        // zum naechsten aktiven Note-Ziel durch).
        if (isNote && this.view._paneFilterMode === null) {
            while (targetLineIndex >= 0 && targetLineIndex < lines.length) {
                const targetLine = lines[targetLineIndex]
                if (targetLine.isPadding) { targetLineIndex += direction; continue }
                const targetPara = this.model.getParagraph(targetLine.paragraphId)
                if (targetPara && targetPara.isOutlineItem()) break
                targetLineIndex += direction
            }
        }

        // Grenzen prüfen - mit speziellem Verhalten an den Dokument-Enden
        if (targetLineIndex < 0) {
            this.model.moveCursorToLineStart()
            this._resetGoalColumn()
            return
        }
        if (targetLineIndex >= lines.length) {
            this.model.moveCursorToLineEnd()
            this._resetGoalColumn()
            return
        }

        // Separator-Zeile: Cursor auf den Separator setzen und Eingabe aktivieren
        const targetLine = lines[targetLineIndex]
        if (targetLine.isSeparator) {
            const oldParagraphId = this.model.cursor.paragraphId
            // Temp-Aktivierung im note-Feld zuruecksetzen bevor wir den
            // Paragraph verlassen — sonst bleibt ein provisorisch aktivierter
            // Absatz faelschlich aktiv, obwohl keine Note eingegeben wurde.
            if (isNote && targetLine.paragraphId !== oldParagraphId) {
                this._checkTempActivationRevert()
            }
            this.model.cursor = new Position(targetLine.paragraphId, 0)
            this.model._markCursorDirty()
            if (targetLine.paragraphId !== oldParagraphId && this.onLeaveParagraph) {
                this.onLeaveParagraph(oldParagraphId)
            }
            // Cursor-Position im Titel aus goalColumn berechnen
            // _separatorClickCharOffset handhabt Proportional (pixelX) und Monospace (x).
            // Feld-spezifische goalCol (info/note) ist in Feld-Koordinaten — fuer das
            // Separator-Label geben wir dann keinen charOffset, Cursor landet am Ende.
            const goalColMatchesLabel = !isInfo && !isNote
            const charOffset = (goalColMatchesLabel && goalCol !== null)
                ? this._separatorClickCharOffset(isProp ? 0 : goalCol, isProp ? goalCol : undefined)
                : undefined
            const pane = this._separatorEditPaneForField(field)
            this._activateSeparatorInput(charOffset, pane)
            return
        }

        // Neue Daten-Position berechnen
        // Proportional: goalCol ist pixelX, Monospace: goalCol ist charX
        // Feld-bewusst: Note/Info haben ihr eigenes Y-Grid (4QL).
        const targetY = this.view._lineToScreenY(targetLineIndex, isInfo ? 'info' : isNote ? 'note' : 'text')
        let newPos
        if (isProp) {
            newPos = isInfo
                ? this.view.infoScreenToData(0, targetY, goalCol)
                : isNote
                ? this.view.noteScreenToData(0, targetY, goalCol)
                : this.view.screenToData(0, targetY, goalCol)
        } else {
            newPos = isInfo
                ? this.view.infoScreenToData(goalCol, targetY)
                : isNote
                ? this.view.noteScreenToData(goalCol, targetY)
                : this.view.screenToData(goalCol, targetY)
        }

        if (newPos) {
            const oldParagraphId = this.model.cursor.paragraphId

            this.model.cursor.paragraphId = newPos.paragraphId
            this.model.cursor.offset = newPos.offset
            this.model._markCursorDirty()

            // Cleanup bei Paragraph-Wechsel
            if (newPos.paragraphId !== oldParagraphId) {
                if (this.onLeaveParagraph) {
                    this.onLeaveParagraph(oldParagraphId)
                }
                if (isNote) {
                    // Alte Temp-Aktivierung zuruecknehmen (falls Note leer blieb)
                    this._checkTempActivationRevert()
                    // Im Filter-Mode: Landung auf provisorischem Passivem aktiviert
                    // ihn temporaer — der User kann sofort tippen. Navigiert er
                    // weiter ohne Eingabe, revertet der naechste Paragraph-Wechsel.
                    if (this.view._paneFilterMode !== null) {
                        const newPara = this.model.getParagraph(newPos.paragraphId)
                        if (newPara && newPara.type !== 'separator' && !newPara.isOutlineItem()) {
                            this._activateTemporarily(newPara)
                        }
                    }
                }
            }
        }
    }

    /**
     * Bewegt den Cursor im info-Feld horizontal, filter-bewusst.
     *
     * Innerhalb eines Absatzes: normale Codepoint-Bewegung via moveCursor.
     * An Absatzgrenzen: sichtbaren Nachbar-Absatz im View suchen (respektiert
     * Pane-Filter und Collapse). Separator-Nachbar → Cursor ins text-Feld
     * (Separator-Input wird vom aufrufenden Handler aktiviert). Nicht-Separator-
     * Nachbar → info-Feld, Offset = Info-Laenge (links) bzw. 0 (rechts).
     *
     * @param {'left'|'right'} direction
     */
    _moveCursorHorizontalInfo(direction) {
        this._moveCursorHorizontalField(direction, 'info')
    }

    /**
     * Bewegt den Cursor im note-Feld horizontal, filter-bewusst.
     * Springt ueber passive Absaetze — analog zur Up/Down-Passive-Skip-Logik.
     * Nur im Filter-Mode sinnvoll; im Main-visible Layout bleibt der alte
     * moveCursor-Pfad aktiv (passive Absaetze sind dort sichtbar).
     *
     * @param {'left'|'right'} direction
     */
    _moveCursorHorizontalNote(direction) {
        this._moveCursorHorizontalField(direction, 'note')
    }

    /**
     * Gemeinsame Implementierung fuer filter-bewusste horizontale Navigation
     * in note/info-Feldern.
     *
     * @param {'left'|'right'} direction
     * @param {'note'|'info'} field
     */
    _moveCursorHorizontalField(direction, field) {
        const cursor = this.model.cursor
        if (!cursor) return
        const para = this.model.getParagraph(cursor.paragraphId)
        if (!para) return

        const fieldText = field === 'note' ? (para.note || '') : (para.info || '')
        const atBoundary = direction === 'left'
            ? cursor.offset === 0
            : cursor.offset === fieldText.length

        if (!atBoundary) {
            this.model.moveCursor(direction)
            return
        }

        // Grenze: sichtbaren Nachbar-Absatz im View suchen. Im Filter-Mode
        // gehoeren provisorisch aufgenommene Passive zu den erlaubten Zielen —
        // bei der Landung im note-Feld werden sie temporaer aktiviert.
        const target = this.view.findVisibleNeighbor(
            cursor.paragraphId,
            direction === 'left' ? -1 : 1
        )
        if (!target) return

        if (target.type === 'separator') {
            // Separator: Cursor ins text-Feld, Separator-Input wird im
            // aufrufenden Handler via _isOnSeparator()-Zweig aktiviert
            cursor.paragraphId = target.id
            cursor.offset = 0
            cursor.field = 'text'
        } else {
            // Nicht-Separator: Feld beibehalten, Offset am Ende (links) / Anfang (rechts)
            const targetText = field === 'note' ? (target.note || '') : (target.info || '')
            cursor.paragraphId = target.id
            cursor.offset = direction === 'left' ? targetText.length : 0
        }
        this.model._markCursorDirty()

        // Temp-Aktivierung-Bookkeeping im note-Feld (Filter-Mode):
        //   - Alte Temp-Aktivierung IMMER reverten bei Paragraph-Wechsel (auch
        //     bei Separator-Ziel, sonst bleibt ein zuvor temporaer aktivierter
        //     Absatz faelschlich aktiv).
        //   - Neue Temp-Aktivierung nur fuer Nicht-Separator-Passive.
        if (field === 'note' && this.view._paneFilterMode !== null) {
            this._checkTempActivationRevert()
            if (target.type !== 'separator' && !target.isOutlineItem()) {
                this._activateTemporarily(target)
            }
        }
    }

    /**
     * Repariert Format-Marker nach einem Paragraph-Split.
     * Nutzt den Original-Text vor dem Split um durchgeschnittene Spans zu heilen:
     * alte Seite bekommt schließende Marker, neue Seite bekommt öffnende Marker.
     * @param {string} originalText - Der vollständige Text VOR dem Split
     * Behandelt Enter (Paragraph splitten).
     */
    _handleEnter() {
        let para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) {
            this.model.splitParagraph()
            return
        }

        const field = this.model.cursor.field || 'text'

        // Info-Feld: Enter blockiert (kein Split)
        if (field === 'info') {
            this.model._actionBlocked = true
            this._pingBlocked()
            return
        }

        // Note-Feld: einfach splitten (keine Listen-Logik)
        // Aber blockieren wenn Abschnitt gesperrt — splitParagraph erzeugt neuen Text-Absatz
        if (field === 'note') {
            if (this.model.isSectionLocked(this.model.cursor.paragraphId)) {
                this._pingBlocked()
                return
            }
            // Outline-Redesign Phase 3b: Pending materialisieren, dann Enter
            // im neuen Outline-Item splittet die (leere) Note → das ist
            // semantisch ein Folge-Outline-Item gleicher Tiefe.
            this._materializePendingForEdit()
            this.model.splitParagraph()
            return
        }

        // Block-Format-basierte Listen-Logik
        this._handleEnterRichText(para)
    }

    /**
     * Enter im Rich-Text-Modus: Block-Format-basierte Listen-Logik.
     * Leerer Listen-Eintrag → Dedent/Liste beenden, sonst normal splitten mit Vererbung.
     */
    _handleEnterRichText(para) {
        if (para.listType) {
            if (para.text.trim() === '') {
                // Leerer Listen-Eintrag: stufenweise dedenten
                if (para.listLevel > 0) {
                    this.model.setBlockFormat(para.id, { listLevel: para.listLevel - 1 })
                } else {
                    // Root-Level: Liste beenden
                    this.model.setBlockFormat(para.id, { listType: null, listLevel: 0 })
                }
                return
            }
        }

        // Heading: Enter setzt neuen Absatz auf normal (Vererbung passiert nicht in _splitParagraphText)
        // Normal splitten — _splitParagraphText() macht bereits Listen-Vererbung (Phase 2)
        const isAtEnd = this.model.cursor.offset >= para.text.length
        this.model.splitParagraph()

        // Neuer Absatz am Ende: Formate zurücksetzen (intuitiver als Vererbung)
        if (isAtEnd) {
            this.model._activeFormats.clear()
        }
    }

    /**
     * Behandelt Shift-Enter: Zeilenumbruch innerhalb des Absatzes (\n einfügen).
     */
    _handleShiftEnter() {
        if (!this.model.cursor) return

        // Selection vorher löschen
        if (this.model.hasSelection()) {
            this.model.deleteSelection()
        }

        this.model.insertText('\n')
    }

    /**
     * Tab im Text-Feld: Listen-Einrückung / Block-Format-Level.
     *
     * Bei einer Selektion über MEHRERE Absätze wird Tab/Shift-Tab auf jeden
     * betroffenen Text-Absatz angewendet (Convenience). Einzelabsatz oder keine
     * Selektion → unverändertes Verhalten (inkl. Ping am Maximum).
     * @param {boolean} isShiftTab
     * @returns {boolean} true wenn behandelt
     */
    _handleTextTab(isShiftTab) {
        if (this.model.hasSelection()) {
            const { start, end } = this.model.selection.getNormalized(this.model)
            const startIndex = this.model.getParagraphIndex(start.paragraphId)
            let endIndex = this.model.getParagraphIndex(end.paragraphId)
            // Endet die Selektion exakt am Absatzanfang, ist dieser Absatz visuell
            // nicht markiert → nicht mit-einrücken (analog Clipboard-Outline-Info).
            if (end.offset === 0 && endIndex > startIndex) endIndex--
            if (endIndex > startIndex) {
                return this._handleTextTabMultiParagraph(startIndex, endIndex, isShiftTab)
            }
        }

        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return false

        // Block-Format-basierter Tab
        return this._handleTextTabRichText(para, isShiftTab)
    }

    /**
     * Tab/Shift-Tab auf alle Absätze einer Mehr-Absatz-Selektion anwenden.
     * Jeder betroffene Text-Absatz bekommt dieselbe Transformation wie ein
     * einzelner Cursor-Absatz — ohne Ping, egal ob sie „wirkt". Separatoren
     * werden übersprungen. Die Einzel-Undos werden zu einem atomaren Schritt
     * zusammengefasst; die Selektion bleibt erhalten (Tab lässt sich wiederholen).
     * @param {number} startIndex
     * @param {number} endIndex
     * @param {boolean} isShiftTab
     * @returns {boolean} immer true (behandelt)
     */
    _handleTextTabMultiParagraph(startIndex, endIndex, isShiftTab) {
        const snapshot = this._takeSnapshot()
        const undoSize = this.model.undoStack.length

        for (let i = startIndex; i <= endIndex; i++) {
            const para = this.model.paragraphs[i]
            if (!para || para.type === 'separator') continue
            this._handleTextTabRichText(para, isShiftTab, true)
        }

        // Mehrere setBlockFormat-Undos zu einem atomaren Schritt zusammenfassen —
        // aber nur wenn überhaupt etwas passiert ist (sonst Phantom-Undo).
        if (this.model.undoStack.length > undoSize) {
            this._collapseUndoEntries(undoSize, snapshot)
        }
        return true
    }

    /**
     * Tab/Shift+Tab im Rich-Text-Modus: Block-Format-Levels ändern.
     * @param {Paragraph} para
     * @param {boolean} isShiftTab
     * @param {boolean} [silent=false] - Unterdrückt den Ping am Maximum (für Batch über mehrere Absätze)
     * @returns {boolean} true wenn behandelt
     */
    _handleTextTabRichText(para, isShiftTab, silent = false) {
        if (para.listType) {
            if (isShiftTab) {
                if (para.listLevel > 0) {
                    this.model.setBlockFormat(para.id, { listLevel: para.listLevel - 1 })
                } else {
                    // Root-Level: Liste komplett entfernen
                    this.model.setBlockFormat(para.id, { listType: null, listLevel: 0 })
                }
            } else {
                if (para.listLevel >= 6) {
                    if (!silent) this._pingBlocked()
                    return true
                }
                this.model.setBlockFormat(para.id, { listLevel: para.listLevel + 1 })
            }
            return true
        }

        if (para.headingLevel > 0) {
            if (isShiftTab) {
                if (para.headingLevel > 1) {
                    this.model.setBlockFormat(para.id, { headingLevel: para.headingLevel - 1 })
                } else {
                    this.model.setBlockFormat(para.id, { headingLevel: 0 })
                }
            } else {
                if (para.headingLevel >= 6) {
                    // H6 + Tab → zurück zu normal
                    this.model.setBlockFormat(para.id, { headingLevel: 0 })
                    return true
                }
                this.model.setBlockFormat(para.id, { headingLevel: para.headingLevel + 1 })
            }
            return true
        }

        if (para.blockquote) {
            if (isShiftTab) {
                this.model.setBlockFormat(para.id, { blockquote: false })
                return true
            }
            return false  // Tab in Blockquote: nicht behandelt
        }

        // Normaler Absatz + Shift+Tab → zum tiefsten Heading (H6) machen
        if (isShiftTab) {
            this.model.setBlockFormat(para.id, { headingLevel: 6 })
            return true
        }

        return false  // Kein Block-Format, Tab → nicht behandelt
    }

    /**
     * Vorschau zu _handleTextTabRichText: Würde Tab (isShiftTab=false) bzw.
     * Shift+Tab (isShiftTab=true) im text-Feld eine echte Block-Format-Wirkung
     * haben? Spiegelt die Verzweigung von _handleTextTabRichText 1:1, damit das
     * Context-Menü ("< Out" / "> In") genau dann aktiv ist, wenn die Taste wirkt.
     * Erkennt Block-Formate über headingLevel/listType/blockquote — NICHT über
     * para.type, das bei Makro-/Tab-Erzeugung 'normal' bleibt.
     * @param {Paragraph} para
     * @param {boolean} isShiftTab
     * @returns {boolean}
     */
    _canTextTab(para, isShiftTab) {
        if (!para) return false
        if (para.listType) {
            // Shift+Tab senkt den Level bzw. entfernt die Liste (immer Wirkung);
            // Tab erhöht nur bis Level 6 (am Maximum pingt es nur, keine Wirkung).
            return isShiftTab || para.listLevel < 6
        }
        if (para.headingLevel > 0) {
            // Beide Richtungen wirken immer — auch H6+Tab (→ normal) und
            // H1+Shift+Tab (→ normal) sind echte Übergänge.
            return true
        }
        if (para.blockquote) {
            // Shift+Tab entfernt das Blockquote; Tab bleibt wirkungslos.
            return isShiftTab
        }
        // Normaler Absatz: Shift+Tab macht H6, Tab bleibt wirkungslos.
        return isShiftTab
    }

    /**
     * Wendet ein Format-ON-Makro an: Marker aus Text entfernen, Format aktivieren.
     * z.B. '*x' → 'x' mit italic ON.
     */
    _applyFormatMacroOn(para, field, fieldText, match) {
        const { marker, formatType, start } = match
        const markerLen = marker.length
        const oldOffset = this.model.cursor.offset
        const paraId = para.id

        // Undo: alten Text + Formate wiederherstellen
        const oldFormats = new Set(this.model._activeFormats)
        this.model._gen++
        const oldParagraphs = this.model.paragraphs.slice()
        const oldCursor = this.model.cursor.clone()
        this.model._pushUndo(() => {
            this.model.paragraphs = oldParagraphs
            this.model.cursor = oldCursor
            this.model._activeFormats = oldFormats
            this.model._markStructuralDirty()
        })
        this.model.charsSinceUndoPoint = 0

        // Marker aus Text entfernen
        const mutPara = this.model._mutate(para)
        const newText = fieldText.slice(0, start) + fieldText.slice(start + markerLen)
        this.model._setFieldText(mutPara, field, newText)

        // RT-Spans anpassen: Marker-Entfernung
        mutPara.formats[field] = adjustSpansForDeleteRange(mutPara.formats[field], start, start + markerLen)
        mutPara.links[field] = adjustLinksForDeleteRange(mutPara.links[field], start, start + markerLen)

        // Cursor anpassen
        this.model.cursor.offset = oldOffset - markerLen
        this.model._activeFormats.add(formatType)

        // Das getippte Zeichen (das den ON-Trigger ausgelöst hat) retroaktiv formatieren.
        // Es wurde VOR dem AutoCorrect-Check via insertText() eingefügt — ohne Format.
        // Nach Marker-Entfernung steht es an Position start, Länge 1.
        mutPara.formats[field] = extendOrCreateSpan(mutPara.formats[field], start, start + 1, formatType)

        // ON-Position merken (für OFF-Makro: Span von hier bis dort)
        this._formatMacroStart.set(formatType, start)

        this.model._markParagraphDirty(paraId)

        // Tracking für Backspace-Undo
        this._autoCorrect.lastReplacement = {
            kind: 'formatOn',
            paragraphId: paraId,
            field,
            offset: this.model.cursor.offset,
            marker,
            formatType
        }
    }

    /**
     * Wendet ein Format-OFF-Makro an: Marker aus Text entfernen, Format-Span schließen.
     * z.B. 'hello*' → 'hello' mit italic OFF + Span.
     */
    _applyFormatMacroOff(para, field, fieldText, match) {
        const { marker, formatType, start } = match
        const markerLen = marker.length
        const paraId = para.id
        const oldOffset = this.model.cursor.offset

        // Undo: alten Text + Formate wiederherstellen
        const oldFormats = new Set(this.model._activeFormats)
        this.model._gen++
        const oldParagraphs = this.model.paragraphs.slice()
        const oldCursor = this.model.cursor.clone()
        this.model._pushUndo(() => {
            this.model.paragraphs = oldParagraphs
            this.model.cursor = oldCursor
            this.model._activeFormats = oldFormats
            this.model._markStructuralDirty()
        })
        this.model.charsSinceUndoPoint = 0

        // Marker aus Text entfernen
        const mutPara = this.model._mutate(para)
        const newText = fieldText.slice(0, start) + fieldText.slice(start + markerLen)
        this.model._setFieldText(mutPara, field, newText)

        // Cursor anpassen (Marker war vor dem Cursor)
        this.model.cursor.offset = oldOffset - markerLen

        // RT-Spans anpassen: Marker-Entfernung + Format-Span erweitern bis hierher
        mutPara.formats[field] = adjustSpansForDeleteRange(mutPara.formats[field], start, start + markerLen)
        mutPara.links[field] = adjustLinksForDeleteRange(mutPara.links[field], start, start + markerLen)

        // Format-Span vom ON-Punkt bis zum OFF-Punkt (= start nach Marker-Entfernung).
        // insertText() hat bereits Spans für Zeichen nach dem ON-Punkt erzeugt,
        // aber der OFF-Marker selbst muss noch nicht enthalten sein.
        // extendOrCreateSpan erweitert bestehende Spans oder erzeugt neue.
        const formatStart = this._formatMacroStart.get(formatType) || 0
        mutPara.formats[field] = extendOrCreateSpan(mutPara.formats[field], formatStart, start, formatType)

        this.model._activeFormats.delete(formatType)
        this._formatMacroStart.delete(formatType)
        this.model._markParagraphDirty(paraId)

        // Tracking für Backspace-Undo
        this._autoCorrect.lastReplacement = {
            kind: 'formatOff',
            paragraphId: paraId,
            field,
            offset: this.model.cursor.offset,
            marker,
            formatType
        }
    }

    /**
     * Wendet eine Emoji-Replacement an (AutoCorrect Emoticon → Emoji).
     */
    _applyEmojiReplacement(para, field, fieldText, match) {
        const oldOffset = this.model.cursor.offset
        const oldParaId = para.id
        // Spans/Links vor der Aenderung sichern (fuer vollstaendigen Undo)
        const oldFormats = para.formats[field] ? para.formats[field].map(s => ({ ...s })) : []
        const oldLinks = para.links[field] ? para.links[field].map(l => ({ ...l })) : []
        const undoFn = () => {
            const p = this.model.getParagraph(oldParaId)
            if (p) {
                this.model._setFieldText(p, field, fieldText)
                const fp = this.model.getParagraph(oldParaId)
                if (fp) {
                    fp.formats[field] = oldFormats
                    fp.links[field] = oldLinks
                }
                this.model.cursor.offset = oldOffset
                this.model._markParagraphDirty(oldParaId)
            }
        }
        this.model._pushUndo(undoFn)
        this.model.charsSinceUndoPoint = 0

        // Text ersetzen: Trigger → Emoji
        const trigger = match.trigger
        const replacement = match.replacement
        const newFieldText = fieldText.slice(0, match.start) + replacement + fieldText.slice(match.start + trigger.length)
        this.model._setFieldText(para, field, newFieldText)
        this.model.cursor.offset = match.start + replacement.length

        // Frische Referenz holen (_setFieldText kann COW-Klon erzeugen)
        const freshPara = this.model.getParagraph(para.id)

        // RT-Spans anpassen: Trigger-Bereich löschen, dann Emoji-Länge einfügen
        freshPara.formats[field] = adjustSpansForDeleteRange(freshPara.formats[field], match.start, match.start + trigger.length)
        freshPara.formats[field] = adjustSpansForInsert(freshPara.formats[field], match.start, replacement.length)
        freshPara.links[field] = adjustLinksForDeleteRange(freshPara.links[field], match.start, match.start + trigger.length)
        freshPara.links[field] = adjustLinksForInsert(freshPara.links[field], match.start, replacement.length)

        this.model._markParagraphDirty(freshPara.id)

        // Tracking für Backspace-Undo
        this._autoCorrect.lastReplacement = {
            paragraphId: para.id,
            field,
            offset: match.start + replacement.length,
            original: trigger,
            replacement
        }
    }

    /**
     * Prüft ob der aktuelle Paragraph-Text ein Block-Format-Makro ist.
     * Nur im RT-Modus, nur wenn der gesamte Text dem Pattern entspricht.
     * Z.B.: "- " → bullet, "1. " → numbered, "# " → h1, "> " → blockquote, "---" → hr.
     */
    _checkBlockFormatMacro() {
        // Overlay-Editoren ohne Block-Strukturen (z.B. Personal-Dict-Pane,
        // Spellcheck §10 Phase 4) deaktivieren Makros via Flag — eine reine
        // Wortliste hat weder Bullets noch Headings/Blockquotes/Code-Bloecke.
        if (this.disableBlockMacros) return
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para || para.type === 'separator') return
        // Nur auf frischen Paragraphen (keine bestehenden Block-Formate)
        if (para.listType || para.headingLevel > 0 || para.blockquote || para.codeBlock || para.type === 'hr') return

        const text = para.text

        // Bullet: "- " oder "* " am Anfang
        if (text.startsWith('- ') || text.startsWith('* ')) {
            this._applyBlockMacro(para, { listType: 'bullet', listLevel: 0 }, 2)
            return
        }

        // Nummerierte Liste: "1. ", "3. ", "4.2.8. " etc. am Anfang
        const numMatch = text.match(/^(\d+(?:\.\d+)*\. )/)
        if (numMatch) {
            const parts = numMatch[1].trimEnd().replace(/\.$/, '').split('.')
            const listLevel = parts.length - 1
            const listStart = parseInt(parts[parts.length - 1], 10) || 1
            this._applyBlockMacro(para, { listType: 'numbered', listLevel, listStart }, numMatch[1].length)
            return
        }

        // Heading: "# " bis "###### " am Anfang
        const headingMatch = text.match(/^(#{1,6}) /)
        if (headingMatch) {
            this._applyBlockMacro(para, { headingLevel: headingMatch[1].length }, headingMatch[0].length)
            return
        }

        // Blockquote: "> " am Anfang
        if (text.startsWith('> ')) {
            this._applyBlockMacro(para, { blockquote: true }, 2)
            return
        }

        // Code-Block: 1-3 Backticks + Space am Anfang.
        // Nur akzeptieren, wenn der Cursor direkt nach dem Trigger steht
        // (User hat gerade Space getippt) — verhindert spurious Match nach
        // Cursor-Sprung in Para-Mitte. Inline-Code-Macro feuert nicht bei
        // Backticks+Space (braucht Zeichen zwischen den Markern), also
        // kollisionsfrei.
        const codeMatch = text.match(/^(`{1,3}) /)
        if (codeMatch && this.model.cursor.offset === codeMatch[0].length) {
            this._applyBlockMacro(para, { codeBlock: true }, codeMatch[0].length)
            return
        }

        // HR: "---" (nur als ganzer Text — kein Restinhalt)
        if (text === '---') {
            this._applyBlockMacro(para, { type: 'hr' }, 3)
            return
        }

        // Separator: "===" (nur als ganzer Text — kein Restinhalt).
        // Nicht bei belegtem Info-Feld — das würde am Separator unsichtbar;
        // der Text bleibt dann einfach stehen.
        if (text === '===' && !para.info) {
            this._applySeparatorMacro(para)
            return
        }
    }

    /**
     * "==="-Makro: Absatz in Separator umwandeln, Cursor ins Label.
     * Den leeren Folge-Absatz (Invariante) stellt das Model sicher.
     * @param {object} para - Paragraph mit Text "==="
     */
    _applySeparatorMacro(para) {
        const sepId = this.model.convertParagraphToSeparator(para.id)
        if (sepId == null) return
        this.model.setCursor(sepId, 0, 'text')
        this._activateSeparatorInput(0, this._separatorBridge.editPaneForField('text'))
    }

    /**
     * Wendet ein Block-Format-Makro an: Makro-Prefix entfernen + Block-Format setzen.
     * Atomarer Undo-Punkt (Text + Format zusammen).
     * @param {object} para - Paragraph
     * @param {object} changes - Block-Format-Änderungen
     * @param {number} prefixLen - Länge des zu entfernenden Prefix (z.B. 2 für "- ")
     */
    _applyBlockMacro(para, changes, prefixLen) {
        const paraId = para.id
        const oldText = para.text
        const oldOffset = this.model.cursor.offset

        // setBlockFormat macht COW + Undo für die Format-Änderung
        this.model.setBlockFormat(paraId, changes)

        // Makro-Prefix entfernen (COW-Snapshot ist schon durch setBlockFormat gemacht)
        const mutPara = this.model._mutate(this.model.getParagraph(paraId))
        if (mutPara) {
            mutPara.text = oldText.slice(prefixLen)
            this.model.cursor.offset = Math.max(0, oldOffset - prefixLen)

            // RT-Spans anpassen: Prefix-Entfernung
            if (prefixLen > 0) {
                mutPara.formats.text = adjustSpansForDeleteRange(mutPara.formats.text, 0, prefixLen)
                mutPara.links.text = adjustLinksForDeleteRange(mutPara.links.text, 0, prefixLen)
            }

            this.model._markParagraphDirty(paraId)

            // Zusammengefasster Undo: Pop den setBlockFormat-Undo, kombinieren
            const formatUndoFn = this.model.undoStack.pop()
            this.model._pushUndo(() => {
                formatUndoFn()
                const p = this.model._mutate(this.model.getParagraph(paraId))
                if (p) {
                    p.text = oldText
                    this.model.cursor.offset = oldOffset
                    this.model._markParagraphDirty(paraId)
                }
            })
        }
    }

    /**
     * Tab/Shift-Tab im Note-Feld: Outline-Tiefe ändern.
     * @param {boolean} isShiftTab
     * @returns {boolean} true wenn behandelt
     */
    _handleNoteTab(isShiftTab) {
        // Collapsed Active: Tab/Shift-Tab gesperrt (siehe spec §5.2). Der
        // Zuordnungsbereich darf sich während collapse nicht verschieben.
        const cursorPara = this.model.getParagraph(this.model.cursor.paragraphId)
        if (cursorPara && cursorPara.isOutlineItem() && cursorPara.activeCollapsed) {
            this._pingBlocked()
            return true
        }

        // Outline-Redesign Phase 3b: Pending-Cursor materialisieren, bevor
        // Tiefenaenderung greift — sonst zielt handleOutlineLeft/Right auf
        // den noch nicht existierenden Para. Auto-Cleanup im Paragraph-
        // Wechsel-Hook deckt den "User leert die Note spaeter wieder"-Fall ab.
        this._materializePendingForEdit()

        if (isShiftTab) {
            const changed = this.model.handleOutlineLeft(this.model.cursor.paragraphId)
            if (!changed) return false  // Kann nicht weiter → nicht behandelt
            // Absatz wurde passiv → Cursor ist jetzt im text-Feld, Main Pane aktivieren
            if (this.model.cursor.field === 'text' && this.onActivatePane) {
                this.onActivatePane('text')
            }
            return true
        } else {
            const changed = this.model.handleOutlineRight(this.model.cursor.paragraphId)
            if (!changed) this._pingBlocked()
            // Absatz wurde passiv → Cursor ist jetzt im text-Feld, Main Pane aktivieren
            if (changed && this.model.cursor.field === 'text' && this.onActivatePane) {
                this.onActivatePane('text')
            }
            return true  // Tab im Note immer behandelt
        }
    }

    /**
     * Prüft ob eine Taste ein druckbares Zeichen ist.
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    _isPrintable(e) {
        return this._charForKey(e) !== null
    }

    /**
     * Liefert das Zeichen fuer eine Taste, oder null fuer nicht-druckbare Tasten.
     * Normalisiert Dead-Keys: Mac DE liefert Backtick als `key='Dead' code='Equal' shift=true`,
     * weil die Akzent-Taste neben "ß" Composition triggern wuerde — Carbisdale hat keinen
     * Composition-Pfad, also biegen wir direkt auf '`' um.
     * @param {KeyboardEvent} e
     * @returns {string|null}
     */
    _charForKey(e) {
        // Keine Modifier (außer Shift und Alt — Alt erzeugt Sonderzeichen wie @, €, etc.)
        if (e.ctrlKey || e.metaKey) return null

        // Mac DE: Shift+Akzent → key='Dead' code='Equal'
        if (e.key === 'Dead' && e.code === 'Equal' && e.shiftKey) return '`'

        // Genau ein Zeichen, kein Steuerzeichen
        if (e.key.length !== 1) return null
        if (e.key.charCodeAt(0) < 32) return null
        return e.key
    }

    /**
     * Prüft ob der Paragraph gewechselt wurde und triggert Cleanup.
     */
    _checkParagraphChange() {
        if (!this.model.cursor) return

        const currentParagraphId = this.model.cursor.paragraphId

        if (this.lastParagraphId !== null && this.lastParagraphId !== currentParagraphId) {
            // Paragraph wurde verlassen
            if (this.onLeaveParagraph) {
                this.onLeaveParagraph(this.lastParagraphId)
            }
            // Temporäre Aktivierung prüfen bei Paragraph-Wechsel
            if (this.model.cursor.field === 'note') {
                this._checkTempActivationRevert()
            }
            // Outline-Redesign Phase 3b §4.4: Auto-Cleanup beim Verlassen.
            // Wird hier UNABHAENGIG von _tempActivation gerufen — auch wenn
            // der User den Auto-Para schon vor laengerer Zeit angelegt + mit
            // Inhalt versehen hat, soll Cleanup greifen, sobald er ihn leer
            // verlaesst. cleanupAutoCreatedIfEmpty ist No-Op wenn paraId
            // nicht im _autoCreatedParagraphIds-Set steht.
            this.model.cleanupAutoCreatedIfEmpty(this.lastParagraphId)

            // Spec §4.3: Ein Pending-Cursor ueberlebt den Paragraph-Wechsel
            // nur, wenn der Cursor weiterhin auf einer Phantom-Main-Zeile
            // steht — dann re-verankern auf das Item unter dem Cursor
            // (Pfeil-Navigation zwischen Phantom-Zeilen). Sonst still
            // raeumen, damit ein spaeterer Edit nicht am alten Anker
            // materialisiert. Temp-Aktivierung (Note-Pending) verwaltet
            // ihr Pending selbst via _checkTempActivationRevert.
            if (!this._tempActivation && this.model.hasPendingCursor()) {
                const cur = this.model.getParagraph(currentParagraphId)
                const onPhantomText = !!cur && cur.isOutlineItem() &&
                    (this.model.cursor.field || 'text') === 'text'
                if (!onPhantomText) {
                    this.model.clearPendingCursor()
                } else if (this.model.getPendingCursor().insertAfterParagraphId !== cur.id) {
                    this.model.setPendingCursor({
                        insertAfterParagraphId: cur.id,
                        depth: 0,
                        type: PARA_TYPE.NORMAL,
                    })
                }
            }
        }

        this.lastParagraphId = currentParagraphId
    }

    // ===== Mouse Handling =====

    /**
     * Behandelt Mouse-Down (Drag-Initialisierung).
     * Wird VOR click aufgerufen und setzt die Startposition für Drag-Selection.
     * @param {number} x - Screen X (relativ zur Pane)
     * @param {number} y - Screen Y (relativ zur Pane)
     * @param {boolean} shiftKey - Shift gedrückt?
     */
    handleMouseDown(x, y, shiftKey, fractionalY, pixelX) {
        // Outline-Selection aufheben bei Main-Pane-Klick
        this.model.clearOutlineSelection()

        // Scrollbar-Klick?
        if (this.view.isOnScrollbar(x)) {
            this.isScrollbarDragging = true
            // Fraktionale Y-Position für pixelgenaues Dragging
            this.scrollbarDragStartY = fractionalY !== undefined ? fractionalY : y
            this.scrollbarDragStartViewport = this.view.viewportStart
            const sbh = this.scrollbarViewHeight

            // Klick auf Track (nicht auf Thumb): Sofort zu dieser Position springen
            if (!this.view.isOnScrollbarThumb(y, sbh)) {
                // Sub-Char-Präzision nutzen, damit der Sprung auf der Minimap pixelgenau landet
                const clickY = fractionalY !== undefined ? fractionalY : y
                this.view.scrollToScrollbarY(clickY, sbh)
                // Neue Startposition für eventuelles Dragging
                this.scrollbarDragStartViewport = this.view.viewportStart
            }
            return
        }

        const pos = this.view.screenToData(x, y, pixelX)
        if (!pos) return

        // Klick auf Separator-Zeile: Cursor setzen, kein Drag
        if (this.model.isSeparator(pos.paragraphId)) {
            const para = this.model.getParagraph(pos.paragraphId)
            if (para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                // Background-Separator: nicht editierbar → Ping
                if (this.onPing) this.onPing()
                return
            }
            // Lock-Control auf Separator-Linie: Klick auf (v)/( ) → Toggle
            const controlX = this.view.width - 4
            if (this.view.width >= 8 && x >= controlX && x <= controlX + 2) {
                this.model.toggleSectionLock(pos.paragraphId)
                this.view.markDirty()
                return
            }
            // Bestehende Model-Selection verwerfen
            this.model.clearSelection()
            this.model.cursor = new Position(pos.paragraphId, 0)
            this.model._markCursorDirty()
            const charOffset = this._separatorClickCharOffset(x, pixelX)
            this._activateSeparatorInput(charOffset)
            // Maus-Drag im Label starten
            this._separatorInput.handleMouseDown(charOffset)
            this._isSeparatorDragging = true
            return
        }

        // Drag-Modus starten, Word-Select-Mode zurücksetzen
        this.isDragging = true
        this.dragStartPos = pos
        this.hadDragMovement = false
        this._wordSelectMode = false
        this._wordSelectAnchorWord = null

        if (shiftKey && this.model.cursor) {
            // Mit Shift: Selection von aktuellem Cursor aus starten/erweitern
            if (!this.model.selection) {
                this.model.startSelection()
            }
        } else {
            // Ohne Shift: Bestehende Selection löschen für neuen Drag
            this.model.clearSelection()
        }
    }

    /**
     * Behandelt einen Mausklick (nach mouseup).
     * @param {number} x - Screen X (relativ zur Pane)
     * @param {number} y - Screen Y (relativ zur Pane)
     * @param {boolean} shiftKey - Shift gedrückt?
     */
    handleClick(x, y, shiftKey, pixelX, opts = {}) {
        // Scrollbar-Klicks wurden bereits in handleMouseDown behandelt
        if (this.view.isOnScrollbar(x)) return

        const pos = this.view.screenToData(x, y, pixelX)
        if (!pos) return

        // Klick auf Separator-Zeile: Separator-Eingabe aktivieren
        if (this.model.isSeparator(pos.paragraphId)) {
            // Nach Drag-Selection im Label: Selection beibehalten
            if (this.justFinishedDrag) {
                this.justFinishedDrag = false
                return
            }
            const para = this.model.getParagraph(pos.paragraphId)
            if (para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                if (this.onPing) this.onPing()
                return
            }
            // Lock-Control: bei Maus wurde der Toggle in handleMouseDown ausgefuehrt
            // (hier nur return). Bei Touch-Tap gibt es kein mousedown — wir
            // toggeln direkt, und akzeptieren Slop, damit das schmale (v)-Symbol
            // ueberhaupt mit dem Finger erreichbar ist.
            const controlX = this.view.width - 4
            const dxSlop = opts.touchSlop ? TOUCH.slop.borderControl.dx : 0
            if (this.view.width >= 8 && x >= controlX - dxSlop && x <= controlX + 2 + dxSlop) {
                if (opts.touchSlop) {
                    this.model.toggleSectionLock(pos.paragraphId)
                    this.view.markDirty()
                }
                return
            }
            // Bestehende Model-Selection verwerfen
            this.model.clearSelection()
            this.model.cursor = new Position(pos.paragraphId, 0)
            this.model._markCursorDirty()
            const charOffset = this._separatorClickCharOffset(x, pixelX)

            // Double/Triple-Click-Erkennung (gleiche Logik wie Textabsätze)
            const now = Date.now()
            const sameSep = this.lastClickPos &&
                this.lastClickPos.paragraphId === pos.paragraphId
            if (sameSep && now - this.lastClickTime < this.DOUBLE_CLICK_THRESHOLD) {
                this.clickCount++
            } else {
                this.clickCount = 1
            }
            this.lastClickTime = now
            this.lastClickPos = pos

            if (this.clickCount >= 4) {
                // Quadruple-Click: ganze Section selektieren (semantisch wie Cmd+A)
                this.model.selectAll()
                this.clickCount = 0
                this.view.markDirty()
                return
            }
            if (this.clickCount === 3) {
                // Triple-Click: ganzes Label selektieren.
                // Kein clickCount-Reset hier — sonst wäre Quadruple-Click nicht erreichbar.
                this._activateSeparatorInput(0)
                this._separatorInput.selectionStart = 0
                this._separatorInput.selectionEnd = this._separatorInput.text.length
                this._separatorInput.cursorPos = this._separatorInput.text.length
                this.view.markDirty()
                return
            }
            if (this.clickCount === 2) {
                // Collapsed Separator: Subtree-Selection statt Wort-Auswahl
                if (para && para.sectionCollapsed) {
                    const idx = this.model.getParagraphIndex(para.id)
                    let endIdx = this.model.paragraphs.length - 1
                    for (let i = idx + 1; i < this.model.paragraphs.length; i++) {
                        const q = this.model.paragraphs[i]
                        if (q.type === 'separator' && q.outlineDepth <= para.outlineDepth) {
                            endIdx = i - 1
                            break
                        }
                    }
                    this.model.setOutlineSelection(idx, endIdx)
                    this.clickCount = 0
                    this.view.markDirty()
                    return
                }
                // Double-Click: Wort unter Cursor selektieren
                this._activateSeparatorInput(charOffset)
                const wb = getWordBounds(this._separatorInput.text, charOffset)
                if (wb) {
                    this._separatorInput.selectionStart = wb.start
                    this._separatorInput.selectionEnd = wb.end
                    this._separatorInput.cursorPos = wb.end
                }
                this.view.markDirty()
                return
            }

            // Single-Click: Cursor setzen
            this._activateSeparatorInput(charOffset)
            this._resetGoalColumn()
            return
        }

        // Goal Column zurücksetzen bei Klick
        this._resetGoalColumn()

        const now = Date.now()
        const samePosition = this.lastClickPos &&
            this.lastClickPos.paragraphId === pos.paragraphId &&
            Math.abs(this.lastClickPos.offset - pos.offset) <= 1

        // Klick-Zählung für Double/Triple-Click
        if (samePosition && now - this.lastClickTime < this.DOUBLE_CLICK_THRESHOLD) {
            this.clickCount++
        } else {
            this.clickCount = 1
        }

        this.lastClickTime = now
        this.lastClickPos = pos

        if (this.clickCount >= 4) {
            // Quadruple-Click: Section bzw. ganzes Notes/Info-Feld (semantisch wie Cmd+A)
            this.model.selectAll()
            this.view.ensureCursorVisible()
            this.clickCount = 0
            return
        }
        if (this.clickCount === 3) {
            // Triple-Click: Paragraph selektieren.
            // Kein clickCount-Reset hier — sonst wäre Quadruple-Click nicht erreichbar.
            this.handleTripleClick(x, y, pixelX)
            return
        }

        if (this.clickCount === 2) {
            // Double-Click: Wort selektieren
            this.handleDoubleClick(x, y, pixelX)
            return
        }

        // v4 Phantom-Main-Klick (Spec §3.4 + §4.3): Klick auf die Phantom-Zeile
        // eines Solo-Outline-Items. Der Klick landet via screenToData auf dem
        // Outline-Item (lines[i].paragraphId = Outline-Item-ID), aber das
        // Outline-Item hat keinen text-Slot. Stattdessen wird ein Pending-
        // Cursor für einen Normal-Para gesetzt — der materialisiert sich beim
        // ersten Tastendruck als neuer Text-Paragraph direkt nach dem Item.
        // Direkter Cursor-Assignment umgeht den setCursor→note-Redirect.
        const clickedPara = !shiftKey ? this.model.getParagraph(pos.paragraphId) : null
        const isPhantomClick = clickedPara && clickedPara.isOutlineItem && clickedPara.isOutlineItem()
        // Jedes Nicht-Phantom-Klick räumt einen ggf. hängenden Pending-Cursor —
        // sonst würde ein späteres Edit den lazy-create an der alten Stelle
        // auslösen.
        if (!isPhantomClick && this.model.hasPendingCursor && this.model.hasPendingCursor()) {
            this.model.clearPendingCursor()
        }
        if (isPhantomClick) {
            this.model.clearSelection()
            this.model.setPendingCursor({
                insertAfterParagraphId: pos.paragraphId,
                depth: 0,
                type: PARA_TYPE.NORMAL,
            })
            this.model.cursor = new Position(pos.paragraphId, 0, 'text')
            this.model._markCursorDirty()
            this.model._selectedLink = null
            this._checkParagraphChange()
            this.view.ensureCursorVisible()
            return
        }

        // Single-Click
        if (shiftKey && this.model.cursor) {
            // Mit Shift: Selection erweitern
            if (!this.model.selection) {
                this.model.startSelection()
            }
            this.model.setCursor(pos.paragraphId, pos.offset)
            this._snapOverRTLink()
            this.model.extendSelection()
        } else if (this.justFinishedDrag) {
            // Nach Drag-Selection: Selection beibehalten, nur Flag zurücksetzen
            this.justFinishedDrag = false
        } else {
            // Link-Klick: direkt aus screenToData-Ergebnis erkennen
            if (pos.isLink) {
                const sepMap = this.view._separatorMap
                const isBroken = !sepMap || !sepMap.has(pos.linkTargetId)
                if (!isBroken) {
                    this._navigateToLink(pos.linkTargetId)
                    return
                }
                this._pingBlocked()
                return
            }

            // URL-Klick: Web-Link im neuen Tab öffnen
            if (pos.isUrl) {
                this._openExternalUrl(pos.url)
                return
            }

            // Ohne Shift: Cursor setzen, Selection löschen
            this.model.clearSelection()
            this.model.setCursor(pos.paragraphId, pos.offset)
            this._snapOverRTLink()
        }

        this.model._selectedLink = null
        this._checkParagraphChange()
        this.view.ensureCursorVisible()
    }

    /**
     * Selektiert den ganzen text-Absatz (Cursor auf Pos0, Anchor auf Pos0 des
     * Folgeabsatzes — beim Ausschneiden geht genau der Absatz weg; letzter
     * Absatz: nur dessen Text). Geteilt von Marker-Doppelklick und dem
     * Context-Menu "Select".
     * @param {string} paragraphId
     */
    selectWholeParagraph(paragraphId) {
        const para = this.model.getParagraph(paragraphId)
        if (!para) return
        const paraIndex = this.model.getParagraphIndex(paragraphId)
        const nextPara = this.model.getParagraphAt(paraIndex + 1)
        if (nextPara) {
            this.model.setSelection(
                new Position(nextPara.id, 0),
                new Position(paragraphId, 0)
            )
        } else {
            this.model.setSelection(
                new Position(paragraphId, para.text.length),
                new Position(paragraphId, 0)
            )
        }
    }

    /**
     * Behandelt einen Doppelklick (Wort selektieren).
     * @param {number} x
     * @param {number} y
     */
    handleDoubleClick(x, y, pixelX) {
        const pos = this.view.screenToData(x, y, pixelX)
        if (!pos) return

        const para = this.model.getParagraph(pos.paragraphId)
        if (!para) return

        // Doppelklick auf Collapse-Host: gesamten Subtree als Outline-Selection
        // (spec §5.2/§5.1 — "exakt wie Doppelklick auf > in Outline").
        if (para.isOutlineItem() && para.activeCollapsed) {
            const idx = this.model.getParagraphIndex(pos.paragraphId)
            const range = this.model.getSubtreeRange(idx)
            this.model.setOutlineSelection(range.startIndex, range.endIndex)
            this.view.ensureCursorVisible()
            return
        }
        if (para.type === 'separator' && para.sectionCollapsed) {
            const idx = this.model.getParagraphIndex(pos.paragraphId)
            let endIdx = this.model.paragraphs.length - 1
            for (let i = idx + 1; i < this.model.paragraphs.length; i++) {
                const p = this.model.paragraphs[i]
                if (p.type === 'separator' && p.outlineDepth <= para.outlineDepth) {
                    endIdx = i - 1
                    break
                }
            }
            this.model.setOutlineSelection(idx, endIdx)
            this.view.ensureCursorVisible()
            return
        }

        // Doppelklick auf Absatzmarke ">": ganzen Absatz selektieren
        if (x < this.view.markerWidth && this.model.cursor && this.model.cursor.field === 'text') {
            this.selectWholeParagraph(pos.paragraphId)
            this.view.ensureCursorVisible()
            return
        }

        // Wort-Grenzen finden
        const wordBounds = getWordBounds(para.text, pos.offset)
        if (wordBounds) {
            this.model.setSelection(
                new Position(pos.paragraphId, wordBounds.start),
                new Position(pos.paragraphId, wordBounds.end)
            )
        }
        this.view.ensureCursorVisible()
    }

    /**
     * Behandelt einen Triple-Click (Paragraph selektieren).
     * @param {number} x
     * @param {number} y
     */
    handleTripleClick(x, y, pixelX) {
        const pos = this.view.screenToData(x, y, pixelX)
        if (!pos) return

        const para = this.model.getParagraph(pos.paragraphId)
        if (!para) return

        // Ganzen Paragraph selektieren
        this.model.setSelection(
            new Position(pos.paragraphId, 0),
            new Position(pos.paragraphId, para.text.length)
        )
        this.view.ensureCursorVisible()
    }

    /**
     * Erweitert Wort-Grenzen um angrenzende Format-Marker.
     * Nur wenn das Wort der GESAMTE Content eines Spans ist (beide Marker direkt angrenzend).
     * Iteriert für verschachtelte Spans (z.B. ***bold-italic***).
     *
     * @param {number} paragraphId
     * @param {number} start - Wort-Start (Data-Offset)
     * @param {number} end - Wort-Ende (Data-Offset)
     * @param {string} field - 'text' oder 'note'
     * @returns {{start: number, end: number}}
     */
    // ===== Auto-Scroll bei Drag über Viewport-Grenzen =====

    /**
     * Startet Auto-Scroll in die angegebene Richtung.
     * @param {number} direction - -1 (hoch) oder +1 (runter)
     * @param {number} distance - Abstand vom Viewport-Rand in Zeilen
     * @param {number} screenX - Letzte X-Position (screen)
     * @param {number|null} pixelX - Letzte pixelX (proportional)
     */
    _startAutoScroll(direction, distance, screenX, pixelX) {
        this._autoScrollDirection = direction
        this._autoScrollSpeed = distance
        this._lastMouseX = screenX
        this._lastMousePixelX = pixelX

        if (this._autoScrollTimer !== null) return  // Timer läuft schon

        this._autoScrollTimer = setInterval(() => this._autoScrollTick(), 80)
    }

    /**
     * Stoppt den Auto-Scroll-Timer.
     */
    _stopAutoScroll() {
        if (this._autoScrollTimer !== null) {
            clearInterval(this._autoScrollTimer)
            this._autoScrollTimer = null
        }
        this._autoScrollDirection = 0
        this._autoScrollSpeed = 0
    }

    /**
     * Ein Auto-Scroll-Schritt: scrollt den Viewport und erweitert die Selection.
     */
    _autoScrollTick() {
        if (!this.isDragging && !this._isNoteDragging && !this._isInfoDragging) {
            this._stopAutoScroll()
            return
        }

        // Raketen-Selektion: exponentiell schneller je weiter vom Rand
        const d = this._autoScrollSpeed
        const speed = d >= 4 ? 40 : d >= 3 ? 10 : d >= 2 ? 3 : 1
        this.view.scrollBy(this._autoScrollDirection * speed)

        // Position am Viewport-Rand bestimmen
        const edgeY = this._autoScrollDirection < 0 ? 0 : this.view.height - 1

        if (this._isInfoDragging) {
            // Info-Drag: Selection innerhalb des gleichen Paragraphs erweitern
            this._autoScrollTickField(edgeY, 'info')
        } else if (this._isNoteDragging) {
            // Note-Drag: Selection innerhalb des gleichen Paragraphs erweitern
            this._autoScrollTickField(edgeY, 'note')
        } else {
            // Main-Drag: Normale oder Word-Snap-Selection erweitern
            this._autoScrollTickMain(edgeY)
        }
    }

    /**
     * Auto-Scroll-Tick für Main-Pane: Erweitert Selection zum Viewport-Rand.
     */
    _autoScrollTickMain(edgeY) {
        const pos = this.view.screenToData(this._lastMouseX, edgeY, this._lastMousePixelX)
        if (!pos) return

        if (this._wordSelectMode && this._wordSelectAnchorWord) {
            // Word-Snap: Cursor auf Wortgrenze setzen
            const cursorWord = this.view.getDisplayWordBoundsWithPunctuation(pos.paragraphId, pos.offset)
            const anchorWord = this._wordSelectAnchorWord
            const anchorIdx = this.model.getParagraphIndex(anchorWord.paragraphId)
            const cursorIdx = this.model.getParagraphIndex(pos.paragraphId)
            const forward = cursorIdx > anchorIdx ||
                (cursorIdx === anchorIdx && pos.offset >= anchorWord.start)

            let anchorPos, cursorPos
            if (forward) {
                anchorPos = new Position(anchorWord.paragraphId, anchorWord.start)
                cursorPos = cursorWord
                    ? new Position(pos.paragraphId, cursorWord.end)
                    : new Position(pos.paragraphId, pos.offset)
            } else {
                anchorPos = new Position(anchorWord.paragraphId, anchorWord.end)
                cursorPos = cursorWord
                    ? new Position(pos.paragraphId, cursorWord.start)
                    : new Position(pos.paragraphId, pos.offset)
            }

            this.model.setSelection(anchorPos, cursorPos)
            this.model.selection.isWordMode = true
        } else {
            // Normaler Drag
            this.model.setCursor(pos.paragraphId, pos.offset)
            this.model.extendSelection()
        }

        // Selection darf nicht über Separator-Grenzen hinausgehen
        this._clampSelectionToSection()
    }

    /**
     * Auto-Scroll-Tick für Note/Info-Pane: Erweitert Selection innerhalb des Paragraphs.
     * @param {number} edgeY - Y-Position am Viewport-Rand
     * @param {'note'|'info'} field - Welches Feld
     */
    _autoScrollTickField(edgeY, field) {
        const dragStartPos = field === 'note' ? this._noteDragStartPos : this._infoDragStartPos
        if (!dragStartPos) return

        const fieldLines = field === 'note' ? this.view.noteLines : this.view.infoLines
        const lineIndex = this.view.screenYToLineIndex(edgeY, field)
        if (lineIndex < 0 || lineIndex >= fieldLines.length) return

        const line = fieldLines[lineIndex]
        if (!line) return
        // Nur innerhalb des gleichen Paragraphs (keine Cross-Paragraph-Selection)
        if (line.paragraphId !== dragStartPos.paragraphId) return

        // Am oberen Rand: Offset am Zeilenanfang, am unteren Rand: am Zeilenende
        const offset = this._autoScrollDirection < 0 ? line.startOffset : line.endOffset

        this.model.setCursor(dragStartPos.paragraphId, offset, field)
        this.model.extendSelection()
    }

    /**
     * Behandelt Maus-Bewegung (für Drag-Selection und Scrollbar-Drag).
     * @param {number} x
     * @param {number} y
     */
    handleMouseMove(x, y, fractionalY, pixelX) {
        // Scrollbar-Drag?
        if (this.isScrollbarDragging) {
            const currentY = fractionalY !== undefined ? fractionalY : y
            const deltaY = currentY - this.scrollbarDragStartY
            this.view.scrollByThumbDrag(deltaY, this.scrollbarDragStartViewport, this.scrollbarViewHeight)
            return
        }

        // Separator-Label-Drag: an SingleLineInput delegieren
        if (this._isSeparatorDragging) {
            const charOffset = this._separatorClickCharOffset(x, pixelX)
            this._separatorInput.handleMouseMove(charOffset)
            this.view.markDirty()
            return
        }

        if (!this.isDragging || !this.dragStartPos) return

        // Auto-Scroll bei Drag über Viewport-Grenzen
        if (y < 0) {
            const distance = Math.abs(y)
            this._startAutoScroll(-1, distance, x, pixelX)
            this.hadDragMovement = true
            return
        } else if (y >= this.view.height) {
            const distance = y - this.view.height + 1
            this._startAutoScroll(1, distance, x, pixelX)
            this.hadDragMovement = true
            return
        } else {
            this._stopAutoScroll()
        }

        const pos = this.view.screenToData(x, y, pixelX)
        if (!pos) return

        // Merken dass tatsächlich eine Drag-Bewegung stattfand
        this.hadDragMovement = true

        // Wörter zwischen Drag-Start und aktueller Position zählen
        const touchedWords = this._countTouchedWords(this.dragStartPos, pos)

        if (touchedWords >= 3 && !this._wordSelectMode) {
            // Eintritt in Word-Select-Mode
            this._wordSelectMode = true
            const anchorBounds = this.view.getDisplayWordBoundsWithPunctuation(
                this.dragStartPos.paragraphId, this.dragStartPos.offset
            )
            if (anchorBounds) {
                this._wordSelectAnchorWord = {
                    paragraphId: this.dragStartPos.paragraphId,
                    start: anchorBounds.start,
                    end: anchorBounds.end
                }
            }
        } else if (touchedWords < 3 && this._wordSelectMode) {
            // Austritt aus Word-Select-Mode
            this._wordSelectMode = false
            this._wordSelectAnchorWord = null
        }

        if (this._wordSelectMode && this._wordSelectAnchorWord) {
            // Word-Snap: Anker und Cursor auf Wortgrenzen
            const anchorWord = this._wordSelectAnchorWord
            const cursorWord = this.view.getDisplayWordBoundsWithPunctuation(pos.paragraphId, pos.offset)

            // Richtung bestimmen
            const anchorIdx = this.model.getParagraphIndex(anchorWord.paragraphId)
            const cursorIdx = this.model.getParagraphIndex(pos.paragraphId)
            const forward = cursorIdx > anchorIdx ||
                (cursorIdx === anchorIdx && pos.offset >= anchorWord.start)

            let anchorPos, cursorPos
            if (forward) {
                anchorPos = new Position(anchorWord.paragraphId, anchorWord.start)
                cursorPos = cursorWord
                    ? new Position(pos.paragraphId, cursorWord.end)
                    : new Position(pos.paragraphId, pos.offset)
            } else {
                anchorPos = new Position(anchorWord.paragraphId, anchorWord.end)
                cursorPos = cursorWord
                    ? new Position(pos.paragraphId, cursorWord.start)
                    : new Position(pos.paragraphId, pos.offset)
            }

            this.model.setSelection(anchorPos, cursorPos)
            this.model.selection.isWordMode = true
        } else {
            // Normaler Drag (zeichengenau)
            if (!this.model.selection) {
                this.model.setSelection(
                    new Position(this.dragStartPos.paragraphId, this.dragStartPos.offset),
                    new Position(pos.paragraphId, pos.offset)
                )
            } else {
                this.model.setCursor(pos.paragraphId, pos.offset)
                this.model.extendSelection()
            }
        }

        // Selection darf nicht über Separator hinausgehen
        this._clampSelectionToSection()
    }

    /**
     * Behandelt Mouse-Up (Drag-Selection oder Scrollbar-Drag beenden).
     * @param {number} x
     * @param {number} y
     */
    handleMouseUp(x, y) {
        // Auto-Scroll stoppen
        this._stopAutoScroll()

        // Scrollbar-Drag beenden
        if (this.isScrollbarDragging) {
            this.isScrollbarDragging = false
            return
        }

        // Separator-Label-Drag beenden
        if (this._isSeparatorDragging) {
            const hadSelection = this._separatorInput.hasSelection()
            this._separatorInput.handleMouseUp()
            this._isSeparatorDragging = false
            // Flag setzen damit handleClick die Selection nicht zerstört
            if (hadSelection) this.justFinishedDrag = true
            this.view.markDirty()
            return
        }

        // Wenn tatsächlich ein Drag mit Bewegung stattfand und eine NICHT-collapsed
        // Selection existiert, Flag setzen damit handleClick (kommt NACH mouseup) die
        // Selection nicht löscht. Eine collapsed Selection (Anker == Cursor) entsteht
        // bei Mikro-Mausbewegung im selben Charakter und darf den nachfolgenden
        // Link-Klick nicht unterdrücken.
        if (this.hadDragMovement && this.model.selection && !this.model.selection.isCollapsed()) {
            this.justFinishedDrag = true
        }
        this.isDragging = false
        this.dragStartPos = null
        this.hadDragMovement = false
    }

    /**
     * Behandelt Mouse-Wheel/Trackpad/Touch (Pixel-genau).
     * Keine Quantisierung mehr: _viewportPixelOffset in EditorView nimmt den
     * Bruchteil direkt auf → smoothes Scrollen auf Retina-Displays.
     * @param {number} deltaY - Scroll-Betrag
     * @param {number} deltaMode - 0=Pixel (häufig), 1=Lines (Firefox), 2=Pages
     */
    handleMouseWheel(deltaY, deltaMode) {
        const charHeight = this.view.renderer?.charHeight || 24
        let pixels
        if (deltaMode === 1) {
            // LINE-Mode: eine "Zeile" ≈ scrollPixelsPerLine Pixel (Firefox-Konvention)
            pixels = deltaY * TIMING.scrollPixelsPerLine
        } else if (deltaMode === 2) {
            // PAGE-Mode: eine "Seite" = Viewport-Höhe
            pixels = deltaY * (this.view.height || 20) * charHeight
        } else {
            // PIXEL-Mode: 1:1 durchreichen — passt sowohl für Trackpad als auch Touch
            pixels = deltaY
        }

        // Sicherung gegen riesige Bursts (Trackpad kann bei manchen Gesten extrem große Deltas liefern)
        const maxPx = TIMING.scrollMaxLinesPerEvent * charHeight
        pixels = Math.sign(pixels) * Math.min(Math.abs(pixels), maxPx)

        if (pixels !== 0) this.view.scrollByPixels(pixels)
    }

    // ===== Clipboard =====

    /**
     * Sammelt Outline-Info für vollständig selektierte Absätze.
     * @returns {Object[]|null} Array mit Block-Format-Info pro Zeile, oder null
     */
    _collectOutlineInfo() {
        if (!this.model.hasSelection()) return null

        const { start, end } = this.model.selection.getNormalized(this.model)
        const startIndex = this.model.getParagraphIndex(start.paragraphId)
        const endIndex = this.model.getParagraphIndex(end.paragraphId)

        // Indizierung ist ABSATZ-basiert (1:1 zu paragraphTexts/rtData und zu
        // EditorModel.pasteText), NICHT zeilen-basiert. Frühere Zeilen-Indizierung
        // (text.split('\n')) verrutschte bei weichen Umbrüchen, weil ein Absatz dann
        // mehrere Zeilen belegte — Block-Attribute landeten am falschen Absatz.
        const outlineInfo = {}

        // Mittlere Absätze sind immer vollständig selektiert → Attribute übernehmen.
        for (let i = startIndex + 1; i < endIndex; i++) {
            outlineInfo[i - startIndex] = this._collectParagraphInfo(this.model.paragraphs[i])
        }

        const firstPara = this.model.paragraphs[startIndex]
        const lastPara = this.model.paragraphs[endIndex]

        if (startIndex === endIndex) {
            // Ein-Absatz-Selection: Block-Attribute nur bei VOLL-Selektion des Absatzes
            // und nur wenn er überhaupt ein Block-Format trägt (Überschrift/Liste/
            // Blockquote/Code). So bleibt ein einzelner Heading-Absatz beim Copy als
            // <h2> erhalten, während eine Teilauswahl normalen Texts unverpackt bleibt.
            if (start.offset === 0 && end.offset > 0 && end.offset === firstPara.text.length
                && this._hasBlockFormat(firstPara)) {
                outlineInfo[0] = this._collectParagraphInfo(firstPara)
            }
        } else {
            // Erster Absatz: vollständig ab Anfang selektiert (start.offset === 0)
            if (start.offset === 0) {
                outlineInfo[0] = this._collectParagraphInfo(firstPara)
            }
            // Letzter Absatz: bis zum Ende selektiert. Guard end.offset > 0 — bei offset 0
            // wird kein Inhalt aus dem Absatz kopiert, seine Attribute gehören nicht rein.
            if (end.offset > 0 && end.offset === lastPara.text.length) {
                outlineInfo[endIndex - startIndex] = this._collectParagraphInfo(lastPara)
            }
        }

        return Object.keys(outlineInfo).length > 0 ? outlineInfo : null
    }

    /**
     * Prüft, ob ein Absatz ein Block-Format trägt (Überschrift, Liste, Blockquote,
     * Codeblock). Normaler Fließtext ohne Block-Format liefert false.
     * @param {Paragraph} para
     * @returns {boolean}
     */
    _hasBlockFormat(para) {
        return (para.headingLevel >= 1) || !!para.listType || !!para.blockquote || !!para.codeBlock
    }

    /**
     * Sammelt Absatz-Attribute (Outline + Block-Format) für die Zwischenablage.
     * @param {Paragraph} para - Der Absatz
     * @returns {Object} Outline- und Block-Format-Daten
     */
    _collectParagraphInfo(para) {
        return {
            type: para.type,
            headingLevel: para.headingLevel,
            listType: para.listType,
            listLevel: para.listLevel,
            listStart: para.listStart,
            blockquote: para.blockquote,
            codeBlock: para.codeBlock
        }
    }

    /**
     * Sammelt die Absatz-Texte der Selection als Array.
     * Soft-Linebreaks (\n) bleiben innerhalb der Absatz-Texte erhalten.
     */
    _collectParagraphTexts(start, end, field) {
        const startIndex = this.model.getParagraphIndex(start.paragraphId)
        const endIndex = this.model.getParagraphIndex(end.paragraphId)
        if (startIndex === endIndex) {
            const para = this.model.paragraphs[startIndex]
            const fieldText = this.model._getFieldText(para, field)
            return [fieldText.substring(start.offset, end.offset)]
        }
        const texts = []
        texts.push(this.model.paragraphs[startIndex].text.substring(start.offset))
        for (let i = startIndex + 1; i < endIndex; i++) {
            texts.push(this.model.paragraphs[i].text)
        }
        texts.push(this.model.paragraphs[endIndex].text.substring(0, end.offset))
        return texts
    }

    /**
     * Behandelt Copy.
     * @returns {string} Der kopierte Text
     */
    handleCopy() {
        // Separator-Label-Selection: Text aus SingleLineInput kopieren
        if (this._isOnSeparator() && this._separatorInput.hasSelection()) {
            const text = this._separatorInput.getSelectedText()
            this._internalClipboard = null
            return text
        }

        // Outline-Selection: komplette Absätze kopieren
        if (this.model.hasOutlineSelection()) {
            this._copyOutlineSelection()
            return this._getOutlineSelectionText()
        }

        if (!this.model.hasSelection()) return ''
        const { start, end } = this.model.selection.getNormalized(this.model)
        const field = start.field || 'text'
        let text = this.model.getSelectedText()

        // RT-Pfad: kein Healing/Stripping (keine Marker im Text)
        const outlineInfo = this._collectOutlineInfo()
        const rtData = this._collectRTDataForCopy(start, end, field)
        // Absatz-Texte als Array speichern: Soft-Linebreaks (\n innerhalb eines Absatzes)
        // bleiben erhalten und werden beim Paste nicht als Absatzgrenzen interpretiert
        const paragraphTexts = this._collectParagraphTexts(start, end, field)
        if (text) {
            this._internalClipboard = { text, outlineInfo, rtData, paragraphTexts }
        }
        return text
    }

    /**
     * Sammelt RT-Daten (formats/links) für den kopierten Bereich.
     * @param {Position} start - Normalisierter Start
     * @param {Position} end - Normalisiertes Ende
     * @param {string} field - Aktives Feld
     * @returns {Array<{formats: Array, links: Array}>}
     */
    _collectRTDataForCopy(start, end, field) {
        if (start.paragraphId === end.paragraphId) {
            const para = this.model.getParagraph(start.paragraphId)
            if (!para) return []
            return [{
                formats: getSpansInRange(para.formats[field] || [], start.offset, end.offset),
                links: getLinksInRange(para.links[field] || [], start.offset, end.offset)
            }]
        }

        // Multi-Paragraph
        const rtData = []
        const startIdx = this.model.getParagraphIndex(start.paragraphId)
        const endIdx = this.model.getParagraphIndex(end.paragraphId)

        for (let i = startIdx; i <= endIdx; i++) {
            const para = this.model.paragraphs[i]
            if (para.type === 'separator') {
                rtData.push({ formats: [], links: [] })
                continue
            }
            const f = (i === startIdx) ? field : 'text'
            const paraText = this.model._getFieldText(para, f)
            const from = (i === startIdx) ? start.offset : 0
            const to = (i === endIdx) ? end.offset : paraText.length

            rtData.push({
                formats: getSpansInRange(para.formats[f] || [], from, to),
                links: getLinksInRange(para.links[f] || [], from, to)
            })
        }
        return rtData
    }

    /**
     * Behandelt Cut.
     * @returns {string} Der ausgeschnittene Text
     */
    handleCut() {
        // Separator-Label-Selection: Text aus SingleLineInput ausschneiden
        // Kein Lock-Check — der Lock gilt für Section-Inhalt, nicht für den Separator-Titel
        if (this._isOnSeparator() && this._separatorInput.hasSelection()) {
            const text = this._separatorInput.getSelectedText()
            this._separatorInput._deleteSelection()
            this._separatorInput._onChange(this._separatorInput.text)
            this._internalClipboard = null
            this.view.markDirty()
            return text
        }

        // Outline-Selection: komplette Absätze ausschneiden
        if (this.model.hasOutlineSelection()) {
            this._copyOutlineSelection()
            const text = this._getOutlineSelectionText()
            const sel = this.model._outlineSelection
            this.model.deleteOutlineSubtree(sel.startIndex, sel.endIndex)
            this.view.ensureCursorVisible()
            return text
        }

        // Section Lock / eingeklappter Host: Cut blockieren (Copy bleibt möglich)
        if (this._isEditBlockedAtCursor()) {
            this._pingBlocked()
            return ''
        }

        if (!this.model.hasSelection()) return ''
        const snapshot = this._takeSnapshot()
        const undoSize = this.model.undoStack.length

        // 1. Clipboard befüllen (VOR Deletion — Spans noch intakt)
        const text = this.handleCopy()
        const { start, end } = this.model.selection.getNormalized(this.model)

        // Cut: aktive Outline-Absätze behalten (Text leeren), passive entfernen
        this.model.cutRange(start, end)
        this._ensureSmartBlanks()
        this._collapseUndoEntries(undoSize, snapshot)
        return text
    }

    /**
     * Behandelt Paste.
     * @param {string} text - Der einzufügende Text
     */
    handlePaste(text, html) {
        if (!text && !html) return

        // Outline-Content-Paste: komplette Absatz-Strukturen einfügen
        if (this._internalClipboard && this._internalClipboard.isOutlineContent) {
            return this._handleOutlinePaste()
        }

        // Section Lock / eingeklappter Host: Paste blockieren
        if (this._isEditBlockedAtCursor()) {
            this._pingBlocked()
            return
        }

        // Auf Separator: Paste an SingleLineInput delegieren (nur Plain-Text)
        if (this._isOnSeparator()) {
            const para = this.model.getParagraph(this.model.cursor.paragraphId)
            if (para && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return
            this._separatorInput.handlePaste(text || '')
            return
        }

        // Phantom-Main-Zeile: Edit-Ziel HIER materialisieren (Lesson 103) —
        // _handlePasteRT verankert Start-Position und RT-Span-Anwendung am
        // Cursor, BEVOR model.pasteText laeuft; der Model-Guard in pasteText
        // kaeme dafuer zu spaet (Spans wuerden aufs Outline-Item zielen).
        this.model._materializePhantomTextTarget()

        // RT-Pfad: kein Marker-Stripping/-Healing nötig
        this._handlePasteRT(text || '', html)
    }

    /**
     * RT-spezifisches Paste: Text einfügen, dann RT-Spans anwenden.
     * @param {string} text - Einzufügender Text (aus Clipboard)
     */
    _handlePasteRT(text, html) {
        const hadSelection = this.model.hasSelection()
        const applySmartBlanks = hadSelection || this._isAtWordBoundary()
        const snapshot = applySmartBlanks ? this._takeSnapshot() : null
        const undoSize = applySmartBlanks ? this.model.undoStack.length : 0

        let insertStartParaId = null
        let insertStartOffset = null
        if (applySmartBlanks && this.model.cursor) {
            if (hadSelection) {
                const { start } = this.model.selection.getNormalized(this.model)
                insertStartParaId = start.paragraphId
                insertStartOffset = start.offset
            } else {
                insertStartParaId = this.model.cursor.paragraphId
                insertStartOffset = this.model.cursor.offset
            }
        }

        // RT-Daten aus internem Clipboard, HTML oder Markdown-Konvertierung
        let rtData = null
        let outlineInfo = null
        let pasteText = text
        let paragraphTexts = null
        if (this._internalClipboard && this._internalClipboard.text === text && this._internalClipboard.rtData) {
            rtData = this._internalClipboard.rtData
            outlineInfo = this._internalClipboard.outlineInfo
            paragraphTexts = this._internalClipboard.paragraphTexts || null
        } else if (html) {
            // Externer Paste mit HTML: Rich-Text-Formate und Block-Attribute extrahieren
            const parsed = htmlToRichText(html)
            rtData = parsed.map(p => ({ formats: p.formats, links: p.links }))
            pasteText = parsed.map(p => p.text).join('\n')

            // Block-Attribute (Listen, Blockquotes, Headings) als outlineInfo aufbauen
            const blockInfo = {}
            for (let bi = 0; bi < parsed.length; bi++) {
                const p = parsed[bi]
                if (p.listType || p.blockquote || p.headingLevel) {
                    blockInfo[bi] = {
                        type: 'normal',
                        headingLevel: p.headingLevel || 0,
                        listType: p.listType || null,
                        listLevel: p.listLevel ?? 0,
                        listStart: 1,
                        blockquote: p.blockquote || false
                    }
                }
            }
            if (Object.keys(blockInfo).length > 0) outlineInfo = blockInfo
        } else {
            // Externer Paste ohne HTML: Markdown → RT konvertieren.
            // markdownLinesToRichText erkennt Fenced-Code-Blocks und liefert
            // codeBlock=true-Einträge (Fence-Marker werden gefiltert).
            const parsed = markdownLinesToRichText(text.split('\n'))
            rtData = parsed.map(p => ({ formats: p.formats, links: p.links }))
            pasteText = parsed.map(p => p.text).join('\n')

            // Block-Attribute (Listen, Blockquotes, Headings, Codeblocks) als outlineInfo.
            const blockInfo = {}
            for (let bi = 0; bi < parsed.length; bi++) {
                const p = parsed[bi]
                if (p.listType || p.blockquote || p.headingLevel || p.codeBlock) {
                    blockInfo[bi] = {
                        type: 'normal',
                        headingLevel: p.headingLevel || 0,
                        listType: p.listType || null,
                        listLevel: p.listLevel ?? 0,
                        listStart: 1,
                        blockquote: p.blockquote || false,
                        codeBlock: p.codeBlock || false
                    }
                }
            }
            if (Object.keys(blockInfo).length > 0) outlineInfo = blockInfo
        }

        // Start-Position vor dem Paste merken
        const startParaId = hadSelection
            ? this.model.selection.getNormalized(this.model).start.paragraphId
            : this.model.cursor.paragraphId
        const startOffset = hadSelection
            ? this.model.selection.getNormalized(this.model).start.offset
            : this.model.cursor.offset
        const startField = this.model.cursor.field || 'text'

        // Text einfügen (konvertierter Text bei externem Paste)
        // paragraphTexts: echte Absatzstruktur aus internem Clipboard
        // (Soft-Linebreaks bleiben innerhalb der Absätze erhalten)
        this.model.pasteText(pasteText, outlineInfo, paragraphTexts)
        if (this.model._actionBlocked) {
            // note/info-Multi-Line-Paste: Inhalt wurde GEFLATTET eingefuegt
            // (Spec "Zentrales Clipboard": Ping + Newlines→Spaces) — der Ping
            // bleibt, aber die Nachverarbeitung muss trotzdem laufen. Vorher
            // brach hier alles ab: RT-Spans gingen verloren und der Cursor
            // konnte offscreen landen (System-Review 2026-06-10). Der
            // Separator-Guard-Fall (nichts eingefuegt) ist durch den
            // separator-Check in _applyPastedRTSpans abgedeckt.
            this._pingBlocked()
            if (startField !== 'text' && rtData && rtData.length > 0) {
                const flatLines = paragraphTexts || pasteText.split('\n')
                this._applyPastedRTSpans(
                    startParaId, startOffset, startField,
                    this._flattenRtForSingleLineField(rtData, flatLines),
                    pasteText.replace(/\n/g, ' '),
                    null
                )
            }
            this._checkParagraphChange()
            this.view.ensureCursorVisible()
            return
        }

        // RT-Spans auf die eingefügten Absätze anwenden
        if (rtData && rtData.length > 0) {
            this._applyPastedRTSpans(startParaId, startOffset, startField, rtData, pasteText, paragraphTexts)
        }

        if (applySmartBlanks) {
            const startOff = (this.model.cursor && this.model.cursor.paragraphId === insertStartParaId)
                ? insertStartOffset : null
            this._ensureSmartBlanks(startOff)
            this._collapseUndoEntries(undoSize, snapshot)
        }

        this._checkParagraphChange()
        this.view.ensureCursorVisible()
    }

    /**
     * Flacht per-Zeile-RT-Daten in EINEN Eintrag ab — fuer den
     * note/info-Multi-Line-Paste, bei dem pasteText alle Newlines durch
     * Spaces ersetzt. Die Spans jeder Quellzeile werden um die kumulierte
     * Laenge der Vorgaengerzeilen (+1 fuer das aus \n gewordene Leerzeichen)
     * verschoben. Soft-Linebreaks INNERHALB eines Eintrags aendern die
     * Laenge beim Flatten nicht (\n → ein Space, 1:1).
     *
     * @param {Array<{formats: Array, links: Array}>} rtData - RT-Daten pro Zeile
     * @param {string[]} lines - die zugehoerigen Quellzeilen
     * @returns {Array<{formats: Array, links: Array}>} Ein-Element-Array fuer _applyPastedRTSpans
     */
    _flattenRtForSingleLineField(rtData, lines) {
        const formats = []
        const links = []
        let base = 0
        for (let i = 0; i < lines.length; i++) {
            const rd = rtData[i]
            if (rd) {
                if (rd.formats && rd.formats.length > 0) formats.push(...shiftSpans(rd.formats, base))
                if (rd.links && rd.links.length > 0) links.push(...shiftLinks(rd.links, base))
            }
            base += lines[i].length + 1
        }
        return [{ formats: mergeAdjacentSpans(formats), links }]
    }

    /**
     * Wendet kopierte RT-Spans auf die eingefügten Absätze an.
     * @param {number} startParaId - Paragraph-ID wo der Paste begann
     * @param {number} startOffset - Offset wo der Paste begann
     * @param {string} field - Feld ('text', 'note', 'info')
     * @param {Array<{formats: Array, links: Array}>} rtData - RT-Daten pro Zeile
     * @param {string} pastedText - Der eingefügte Text
     */
    _applyPastedRTSpans(startParaId, startOffset, field, rtData, pastedText, paragraphTexts = null) {
        const lines = paragraphTexts || pastedText.split('\n')

        if (lines.length === 1) {
            // Single-Paragraph: Spans verschieben und in bestehende mergen
            const para = this.model.getParagraph(startParaId)
            if (!para || para.type === 'separator') return
            const rd = rtData[0]
            if (!rd) return
            if (rd.formats && rd.formats.length > 0) {
                const shifted = shiftSpans(rd.formats, startOffset)
                para.formats[field] = mergeAdjacentSpans([...para.formats[field], ...shifted])
            }
            if (rd.links && rd.links.length > 0) {
                const shifted = shiftLinks(rd.links, startOffset)
                para.links[field] = [...para.links[field], ...shifted]
                    .sort((a, b) => a.offset - b.offset)
            }
            return
        }

        // Multi-Paragraph: Erster, Mittlere, Letzter
        const startIdx = this.model.getParagraphIndex(startParaId)
        for (let i = 0; i < rtData.length && i < lines.length; i++) {
            const paraIdx = startIdx + i
            if (paraIdx >= this.model.paragraphs.length) break
            const para = this.model.paragraphs[paraIdx]
            if (para.type === 'separator') continue
            const rd = rtData[i]
            if (!rd) continue

            const f = (i === 0) ? field : 'text'

            if (i === 0) {
                // Erster Absatz: Spans ab startOffset mergen
                if (rd.formats && rd.formats.length > 0) {
                    const shifted = shiftSpans(rd.formats, startOffset)
                    para.formats[f] = mergeAdjacentSpans([...para.formats[f], ...shifted])
                }
                if (rd.links && rd.links.length > 0) {
                    const shifted = shiftLinks(rd.links, startOffset)
                    para.links[f] = [...para.links[f], ...shifted]
                        .sort((a, b) => a.offset - b.offset)
                }
            } else if (i === rtData.length - 1) {
                // Letzter Absatz: Spans am Anfang mergen (restlicher Text folgt danach)
                if (rd.formats && rd.formats.length > 0) {
                    para.formats[f] = mergeAdjacentSpans([...rd.formats, ...para.formats[f]])
                }
                if (rd.links && rd.links.length > 0) {
                    para.links[f] = [...rd.links, ...para.links[f]]
                        .sort((a, b) => a.offset - b.offset)
                }
            } else {
                // Mittlere Absätze: Spans direkt setzen (neuer Absatz, vorher leer)
                if (rd.formats && rd.formats.length > 0) {
                    para.formats[f] = [...rd.formats]
                }
                if (rd.links && rd.links.length > 0) {
                    para.links[f] = [...rd.links]
                }
            }
        }
    }

    /**
     * Signalisiert eine blockierte Aktion über den Callback (Ping-Alarm).
     */
    _pingBlocked() {
        if (this.onActionBlocked) this.onActionBlocked()
    }

    /**
     * Cmd+Enter: Collapse des aktuellen Hosts togglen.
     * Separator-Paragraph → sectionCollapsed; aktiver Absatz → activeCollapsed;
     * alles andere → Ping (siehe pane-formatting-specification.md §5).
     */
    _handleCollapseToggle() {
        if (!this.model.cursor) return
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) { this._pingBlocked(); return }

        this.model._actionBlocked = false

        if (para.type === 'separator') {
            const newState = this.model.toggleSectionCollapse(para.id)
            if (this.model._actionBlocked || newState === null) {
                this._pingBlocked()
                return
            }
            // Beim Einklappen: Cursor aufs text-Feld (Separator-Label) springen —
            // note/info eines collapsten Separators ist nicht erreichbar.
            if (newState === true) {
                const f = this.model.cursor.field
                if (f !== 'text') {
                    this.model.cursor = new Position(para.id, 0, 'text')
                }
            }
            this.view.markDirty()
            this.view.ensureCursorVisible()
            return
        }

        if (para.isOutlineItem()) {
            const newState = this.model.toggleActiveCollapse(para.id)
            if (this.model._actionBlocked || newState === null) {
                this._pingBlocked()
                return
            }
            // Beim Einklappen: text-Feld darf nur Pos0 tragen.
            if (newState === true) {
                const f = this.model.cursor.field || 'text'
                if (f === 'text' && this.model.cursor.offset !== 0) {
                    this.model.cursor = new Position(para.id, 0, 'text')
                }
            }
            this.view.markDirty()
            this.view.ensureCursorVisible()
            return
        }

        // Weder Separator noch Active — kein Host ableitbar.
        this._pingBlocked()
    }

    /**
     * Prüft ob der Cursor (oder die Selection) in einem gesperrten Abschnitt liegt.
     * Nur für das text-Feld im Main Pane relevant.
     * @returns {boolean}
     */
    /**
     * True wenn Cursor im text-Feld eines collapsten Actives liegt.
     * In diesem Zustand ist Text-Modifikation gesperrt (spec §5.2).
     */
    _isCollapsedActiveTextAtCursor() {
        if (!this.model.cursor) return false
        const field = this.model.cursor.field || 'text'
        if (field !== 'text') return false
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        return !!(para && para.isOutlineItem() && para.activeCollapsed)
    }

    _isLockedAtCursor() {
        if (!this.model.cursor) return false
        const field = this.model.cursor.field || 'text'
        if (field !== 'text') return false
        // Bei Selection: prüfen ob IRGENDEIN Paragraph in der Selection gesperrt ist
        if (this.model.hasSelection()) {
            const sel = this.model.selection
            const anchorIdx = this.model.getParagraphIndex(sel.anchor.paragraphId)
            const cursorIdx = this.model.getParagraphIndex(sel.cursor.paragraphId)
            const startIdx = Math.min(anchorIdx, cursorIdx)
            const endIdx = Math.max(anchorIdx, cursorIdx)
            for (let i = startIdx; i <= endIdx; i++) {
                if (this.model.isSectionLocked(this.model.paragraphs[i].id)) return true
            }
            return false
        }
        return this.model.isSectionLocked(this.model.cursor.paragraphId)
    }

    /**
     * True, wenn die Cursor-Zeile die sichtbare Host-Zeile eines eingeklappten
     * Outline-Items ist (view-seitig mit isCollapsedActiveHost markiert). Deckt
     * besonders den Co-Location-Fall ab, in dem cursor.paragraphId der Normal-Para-
     * Buddy ist (nicht das Outline-Item selbst — den fängt _isCollapsedActiveTextAtCursor).
     * Nur im text-Feld; bei Selection zählt jeder erfasste Absatz.
     */
    _isCollapsedHostAtCursor() {
        if (!this.model.cursor || !this.view) return false
        if ((this.model.cursor.field || 'text') !== 'text') return false
        const isHost = (paraId) => {
            const idx = this.view._paragraphLineIndex.get(paraId)
            if (idx === undefined) return false
            const line = this.view.lines[idx]
            return !!(line && line.isCollapsedActiveHost)
        }
        if (this.model.hasSelection()) {
            const sel = this.model.selection
            const aIdx = this.model.getParagraphIndex(sel.anchor.paragraphId)
            const cIdx = this.model.getParagraphIndex(sel.cursor.paragraphId)
            for (let i = Math.min(aIdx, cIdx); i <= Math.max(aIdx, cIdx); i++) {
                if (isHost(this.model.paragraphs[i].id)) return true
            }
            return false
        }
        return isHost(this.model.cursor.paragraphId)
    }

    /**
     * Edit am Cursor blockiert? Gesperrter Abschnitt ODER eingeklappter Host —
     * beide schützen den (nur teilweise sichtbaren) Text vor Änderungen.
     */
    _isEditBlockedAtCursor() {
        return this._isLockedAtCursor() || this._isCollapsedHostAtCursor()
    }

    /**
     * Maximaler erlaubter Cursor-Offset im eingeklappten Host (= das letzte
     * sichtbare Zeichen vor der "…"), oder null, wenn der Cursor nicht im
     * text-Feld eines Hosts steht. Beim Anhängen ist der ganze (einzeilige) Text
     * sichtbar; beim Ersetzen (Umbruch) verdecken die letzten 3 Zeichen der ersten
     * Zeile die "…", die Grenze liegt also 3 Zeichen früher.
     * @returns {number|null}
     */
    _collapsedHostVisibleEnd() {
        if (!this.model.cursor || !this.view) return null
        if ((this.model.cursor.field || 'text') !== 'text') return null
        const idx = this.view._paragraphLineIndex.get(this.model.cursor.paragraphId)
        if (idx === undefined) return null
        const line = this.view.lines[idx]
        // Nur wenn wirklich Inhalt versteckt ist (Ellipsis): sonst keine Grenze.
        if (!line || !line.isCollapsedActiveHost || !line.collapseHasEllipsis) return null
        const end = line.endOffset != null ? line.endOffset : 0
        return line.collapseEllipsisAppend ? end : Math.max(0, end - 3)
    }

    /**
     * Korrigiert den Cursor, falls er nach einer Bewegung in versteckten
     * (eingeklappten) Text oder hinter das sichtbare Host-Ende geraten ist —
     * zieht ihn auf das sichtbare Ende des zugehörigen Hosts (das letzte Zeichen
     * vor der "…"). Genutzt für ArrowLeft an Pos 0 des Folge-Absatzes (rückwärts
     * ins Versteckte). Gibt true zurück, wenn korrigiert wurde.
     */
    _clampCursorOffHidden() {
        if (!this.model.cursor || !this.view) return false
        if ((this.model.cursor.field || 'text') !== 'text') return false
        const visibleEndOf = (line) => {
            const end = (line && line.endOffset != null) ? line.endOffset : 0
            return (line && line.isCollapsedActiveHost && line.collapseHasEllipsis
                && !line.collapseEllipsisAppend) ? Math.max(0, end - 3) : end
        }
        const pid = this.model.cursor.paragraphId
        const idx = this.view._paragraphLineIndex.get(pid)
        if (idx === undefined) {
            // In einem versteckten Absatz gelandet → zum vorherigen sichtbaren Host
            const mIdx = this.model.getParagraphIndex(pid)
            let j = mIdx - 1
            while (j >= 0 && !this.view._paragraphLineIndex.has(this.model.paragraphs[j].id)) j--
            if (j < 0) return false
            const host = this.model.paragraphs[j]
            const line = this.view.lines[this.view._paragraphLineIndex.get(host.id)]
            this.model.cursor = new Position(host.id, visibleEndOf(line), 'text')
            this.model._markCursorDirty()
            return true
        }
        // Auf dem Host, aber hinter dem sichtbaren Ende → zurückziehen
        const line = this.view.lines[idx]
        if (line && line.isCollapsedActiveHost && line.collapseHasEllipsis) {
            const visEnd = visibleEndOf(line)
            if ((this.model.cursor.offset || 0) > visEnd) {
                this.model.cursor = new Position(pid, visEnd, 'text')
                this.model._markCursorDirty()
                return true
            }
        }
        return false
    }

    // ===== Outline-Selection-Handling =====

    /**
     * Behandelt Tastatureingaben bei aktiver Outline-Selection.
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    _handleOutlineSelectionKey(e) {
        const cmdKey = e.metaKey || e.ctrlKey
        const sel = this.model._outlineSelection

        // Delete/Backspace → Subtree löschen
        if (e.key === 'Delete' || e.key === 'Backspace') {
            this.model.deleteOutlineSubtree(sel.startIndex, sel.endIndex)
            this.view.ensureCursorVisible()
            return true
        }

        // Cmd+C → Kopieren
        if (cmdKey && (e.key === 'c' || e.key === 'C') && !e.shiftKey) {
            this._copyOutlineSelection()
            return true
        }

        // Cmd+X → Ausschneiden
        if (cmdKey && (e.key === 'x' || e.key === 'X') && !e.shiftKey) {
            this._copyOutlineSelection()
            this.model.deleteOutlineSubtree(sel.startIndex, sel.endIndex)
            this.view.ensureCursorVisible()
            return true
        }

        // Cmd+Z → Undo
        if (cmdKey && (e.key === 'z' || e.key === 'Z')) {
            if (e.shiftKey) this.model.redo()
            else this.model.undo()
            this.view.ensureCursorVisible()
            return true
        }

        // Escape → Selection aufheben
        if (e.key === 'Escape') {
            this.model.clearOutlineSelection()
            return true
        }

        // Pfeiltasten → Selection aufheben, Taste durchlassen (Cursor bewegt sich)
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
            e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
            e.key === 'Home' || e.key === 'End') {
            this.model.clearOutlineSelection()
            return false
        }

        // Modifier-Tasten allein → ignorieren (Cmd, Shift, Alt, Ctrl)
        if (e.key === 'Meta' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift') {
            return true
        }

        // Druckbare Zeichen → Subtree löschen, Taste durchlassen (Zeichen wird eingefügt)
        if (e.key.length === 1 && !cmdKey && !e.altKey) {
            this.model.deleteOutlineSubtree(sel.startIndex, sel.endIndex)
            this.view.ensureCursorVisible()
            return false
        }

        // Alle anderen Tasten → Selection aufheben, Taste konsumieren
        this.model.clearOutlineSelection()
        return true
    }

    /**
     * Gibt den Plain-Text der Outline-Selection zurück (für System-Clipboard).
     * @returns {string}
     */
    _getOutlineSelectionText() {
        const sel = this.model._outlineSelection
        if (!sel) return ''
        const texts = []
        for (let i = sel.startIndex; i <= sel.endIndex; i++) {
            const para = this.model.paragraphs[i]
            if (para.type === 'separator') {
                texts.push('--- ' + (para.text || ''))
            } else {
                texts.push(para.text)
            }
        }
        return texts.join('\n')
    }

    /**
     * Kopiert die aktuelle Outline-Selection ins interne Clipboard.
     */
    _copyOutlineSelection() {
        const sel = this.model._outlineSelection
        if (!sel) return
        const clones = []
        for (let i = sel.startIndex; i <= sel.endIndex; i++) {
            clones.push(this.model.paragraphs[i].clone())
        }
        this._internalClipboard = { isOutlineContent: true, paragraphs: clones }
    }

    /**
     * Fügt Outline-Content aus dem internen Clipboard ein.
     * Nur an Position 0 im text-Feld erlaubt (nicht auf Separatoren).
     */
    _handleOutlinePaste() {
        const cursor = this.model.cursor
        if (!cursor) return

        // Nicht auf Separatoren
        if (this.model.isSeparator(cursor.paragraphId)) {
            this._pingBlocked()
            return
        }

        const field = cursor.field || 'text'

        // text-Feld: nur an Position 0 erlaubt
        if (field === 'text' && cursor.offset !== 0) {
            this._pingBlocked()
            return
        }

        // note-Feld: temporäre Aktivierung reverten (Absatz wird wieder passiv)
        if (field === 'note') {
            this._checkTempActivationRevert()
        }

        const insertIndex = this.model.getParagraphIndex(cursor.paragraphId)
        this.model.pasteOutlineSubtree(
            this._internalClipboard.paragraphs,
            insertIndex
        )
        this.view.ensureCursorVisible()
        // Cursor steht nach Paste im text-Feld → Main aktivieren
        return 'activateMain'
    }

    // ===== Section Links & Navigation History =====

    /**
     * Navigiert zurück in der Navigation-History.
     */
    _navigateBack() {
        const currentPos = this._getCurrentHistoryPos()
        const pos = this._navigationHistory.back(currentPos)
        if (!pos) {
            this._pingBlocked()
            return
        }
        this._navigateTo(pos.paragraphId, pos.field, pos.offset, pos.viewportStart)
    }

    /**
     * Navigiert vorwärts in der Navigation-History.
     */
    _navigateForward() {
        const currentPos = this._getCurrentHistoryPos()
        const pos = this._navigationHistory.forward(currentPos)
        if (!pos) {
            this._pingBlocked()
            return
        }
        this._navigateTo(pos.paragraphId, pos.field, pos.offset, pos.viewportStart)
    }

    /**
     * Aktuelle Position als History-Eintrag (für Back/Forward-Speicherung).
     */
    _getCurrentHistoryPos() {
        if (!this.model.cursor) return null
        return {
            paragraphId: this.model.cursor.paragraphId,
            field: this.model.cursor.field || 'text',
            offset: this.model.cursor.offset,
            viewportStart: this.view.viewportStart
        }
    }

    /**
     * Navigiert zu einer bestimmten Position (Absatz, Feld, Offset, Viewport).
     */
    _navigateTo(paragraphId, field, offset, viewportStart) {
        const para = this.model.getParagraph(paragraphId)
        if (!para) {
            this._pingBlocked()
            return
        }
        // Cursor setzen
        const fieldText = this.model._getFieldText(para, field)
        const safeOffset = Math.min(offset, fieldText.length)
        this.model.setCursor(paragraphId, safeOffset, field)
        this.model.clearSelection()
        this.model._selectedLink = null

        // Viewport zeilengenau wiederherstellen
        if (viewportStart !== undefined) {
            this.view.scrollTo(viewportStart)
        } else {
            this.view.ensureCursorVisible()
        }

        // Pane aktivieren
        if (this.onActivatePane) this.onActivatePane(field)

        this.view.markDirty()
    }

    /**
     * Öffnet eine im Text erkannte Web-URL in einem neuen Browser-Tab.
     * noopener/noreferrer: der neue Tab bekommt keinen Zugriff auf window.opener.
     * Geteilt von allen drei Feldern (text/note/info).
     * @param {string} url
     */
    _openExternalUrl(url) {
        if (!url) return
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    /**
     * Navigiert zu einem Separator (für Link-Klick).
     * Pusht die aktuelle Position in die Navigation-History.
     *
     * Viewport-Regeln:
     * - Main sichtbar → Separator = erste unsichtbare Zeile oben, Cursor auf text-Feld
     *   des ersten Absatzes nach Separator, Offset 0
     * - Main nicht sichtbar + Sprung aus Context → wie oben, aber Cursor auf info-Feld
     * - Main nicht sichtbar + Sprung aus Outline → Separator oben sichtbar,
     *   Cursor auf Separator-Label, Offset 0
     */
    _navigateToLink(targetId) {
        if (!this.model.cursor) return

        // Prüfen ob Ziel existiert
        const targetPara = this.model.getParagraph(targetId)
        if (!targetPara || targetPara.type !== 'separator') {
            this._pingBlocked()
            return
        }

        // Quell-Position merken (vor dem Cursor-Wechsel)
        const sourceField = this.model.cursor.field || 'text'
        const fromPos = {
            paragraphId: this.model.cursor.paragraphId,
            field: sourceField,
            offset: this.model.cursor.offset,
            viewportStart: this.view.viewportStart
        }

        this.model.clearSelection()
        this.model._selectedLink = null

        // Ziel-Section + Ancestor-Sections sichtbar machen, falls eingeklappt.
        // Cloud-synchronisiert (_markParagraphDirty), aber kein Undo-Eintrag —
        // semantisch eine Folge der Navigation, nicht eine eigenstaendige Edit-Aktion.
        this.model.expandSectionForLinkTarget(targetId)

        // Lines müssen aktuell sein für Zeilen-Index-Suche
        this.view.rebuildIfDirty()

        const mainVisible = this.isMainVisible()
        const targetIdx = this.model.getParagraphIndex(targetId)

        if (!mainVisible && sourceField === 'note') {
            // Outline-Sprung ohne Main → Separator sichtbar, Cursor auf Label
            const sepLineIdx = this.view.getLineIndexForPosition(targetId, 0)
            if (sepLineIdx >= 0) {
                this.view.scrollTo(sepLineIdx)
            }
            this.model.setCursor(targetId, 0)
        } else {
            // Main sichtbar ODER Context-Sprung ohne Main
            // → Separator = erste unsichtbare Zeile oben, Cursor auf ersten Absatz danach
            const sepLineIdx = this.view.getLineIndexForPosition(targetId, 0)
            if (sepLineIdx >= 0) {
                // Separator-Zeile soll die letzte NICHT sichtbare Zeile sein
                // → viewportStart = sepLineIdx + 1 (Separator-Zeilen = 1)
                const sepLineCount = this._getSeparatorLineCount(targetId)
                this.view.scrollTo(sepLineIdx + sepLineCount)
            }

            // Cursor auf den ersten Absatz nach dem Separator
            const nextPara = (targetIdx >= 0 && targetIdx < this.model.paragraphs.length - 1)
                ? this.model.paragraphs[targetIdx + 1]
                : null

            if (nextPara) {
                const field = (!mainVisible && sourceField === 'info') ? 'info' : 'text'
                this.model.setCursor(nextPara.id, 0, field)
            } else {
                // Kein Absatz nach dem Separator (Edge Case: Separator am Ende)
                this.model.setCursor(targetId, 0)
            }
        }

        // Quell- und Zielposition in History registrieren
        const toPos = {
            paragraphId: this.model.cursor.paragraphId,
            field: this.model.cursor.field || 'text',
            offset: this.model.cursor.offset,
            viewportStart: this.view.viewportStart
        }
        this._navigationHistory.navigate(fromPos, toPos)

        // Ziel-Pane aktivieren
        const targetField = this.model.cursor?.field || 'text'
        if (this.onActivatePane) this.onActivatePane(targetField)

        this.view.markDirty()
    }

    /**
     * Ermittelt wie viele Screen-Zeilen ein Separator einnimmt.
     */
    _getSeparatorLineCount(paragraphId) {
        let count = 0
        const startIdx = this.view._paragraphLineIndex.get(paragraphId)
        if (startIdx === undefined) return 1
        for (let i = startIdx; i < this.view.lines.length; i++) {
            if (this.view.lines[i].paragraphId !== paragraphId) break
            count++
        }
        return Math.max(1, count)
    }

    /**
     * Prüft ob an einer Screen-Position ein Link-Label liegt.
     * @param {number} paragraphId
     * @param {number} dataOffset - Data-Offset im Text
     * @param {string} field
     * @returns {{targetId: number, isBroken: boolean, dataStart: number, dataEnd: number}|null}
     */
    _getLinkAtDataOffset(paragraphId, dataOffset, field = 'text') {
        const para = this.model.getParagraph(paragraphId)
        if (!para) return null

        // RT: Links in para.links[field], \uFFFC am offset
        const links = para.links[field]
        if (!links || links.length === 0) return null
        for (const link of links) {
            if (link.offset === dataOffset) {
                // Broken-Status prüfen
                const sepMap = this.view._separatorMap
                const isBroken = !sepMap || !sepMap.has(link.targetId)
                return { targetId: link.targetId, isBroken, dataStart: link.offset, dataEnd: link.offset + 1 }
            }
        }
        return null
    }

    /**
     * Gibt die Navigation-History zurück (für Tests).
     */
    get navigationHistory() {
        return this._navigationHistory
    }

    // ===== Link Picker — Delegation an LinkPicker-Klasse =====

    /**
     * Gibt den Link-Picker-State zurueck (fuer Overlay-Rendering).
     */
    getLinkPickerState() {
        return this._linkPicker.getState()
    }

    /**
     * Verarbeitet einen Mausklick auf den Link-Picker.
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn konsumiert
     */
    handleLinkPickerClick(charX, charY) {
        return this._linkPicker.handleClick(charX, charY)
    }

    /**
     * Verarbeitet Mouse-Move ueber dem Link-Picker (Hover-Selection).
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn die Maus ueber dem Picker liegt
     */
    handleLinkPickerMouseMove(charX, charY) {
        return this._linkPicker.handleMouseMove(charX, charY)
    }

    /**
     * Verarbeitet ein Mausrad-Event ueber dem Link-Picker (zeilenweiser Scroll).
     * @param {number} deltaLines
     * @returns {boolean} true wenn sich der Scroll-Offset geaendert hat
     */
    handleLinkPickerWheel(deltaLines) {
        return this._linkPicker.handleWheel(deltaLines)
    }

    /**
     * Setter fuer _linkPickerBounds — wird von main.js nach dem Overlay-
     * Render beschrieben. Delegiert an die Picker-Instanz.
     */
    set _linkPickerBounds(value) {
        this._linkPicker.setBounds(value)
    }

    /** Getter fuer Picker-Bounds (fuer Abfragen). */
    get _linkPickerBounds() {
        return this._linkPicker.bounds
    }

    /**
     * Zeigt Bestätigungsdialog zum Entfernen eines Separators.
     * @param {number} sepIndex - Index des Separator-Absatzes
     */
    _confirmSeparatorRemove(sepIndex) {
        if (!this._dialogManager) return
        if (sepIndex < 0 || sepIndex >= this.model.paragraphs.length) return
        if (this.model.paragraphs[sepIndex].type !== 'separator') return
        // Background-Separator kann nicht gelöscht werden
        if (this.model.paragraphs[sepIndex].outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            this._pingBlocked()
            return
        }

        // Prüfen ob Links auf diesen Separator verweisen
        const sepPara = this.model.paragraphs[sepIndex]
        const linksToTarget = this.model.getLinksToTarget(sepPara.id)
        const linkWarning = linksToTarget.length > 0
            ? linksToTarget.length === 1
                ? '\nThis section has 1 link that will become invalid.'
                : `\nThis section has ${linksToTarget.length} links that will become invalid.`
            : ''

        this._dialogManager.showConfirm(
            'Remove separator',
            'Remove section separator?',
            'The sections will be merged.' + linkWarning
        ).then(confirmed => {
            if (confirmed) {
                this.model.removeSeparator(sepIndex)
                this.view.ensureCursorVisible()
            }
        })
    }

    /**
     * Behandelt Backspace an Separator: Der Separator steht VOR dem aktuellen Absatz.
     */
    // ===== Separator-Titel-Eingabe =====

    /**
     * Gibt den State des Separator-Inputs zurück, wenn der Cursor auf einem Separator steht.
     * @returns {{ cursorPos: number, scrollOffset: number, selectionStart: number, selectionEnd: number, text: string, pane: string }|null}
     */
    getSeparatorInputState() {
        if (!this._isOnSeparator()) return null
        const state = this._separatorInput.getState()
        state.pane = this._separatorEditPane
        return state
    }

    /**
     * Prüft ob der Cursor aktuell auf einem Separator steht.
     * @returns {boolean}
     */
    _isOnSeparator() {
        return this.model.cursor && this.model.isSeparator(this.model.cursor.paragraphId)
    }

    /**
     * Delegator: aktiviert die Separator-Eingabe via Bridge.
     * @param {number} [charOffset]
     * @param {string} [pane='main']
     */
    _activateSeparatorInput(charOffset, pane = 'main') {
        this._separatorBridge.activate(charOffset, pane)
    }

    /**
     * Berechnet den Zeichen-Offset im Separator-Titel für einen Mausklick.
     * @param {number} x - Screen X (Grid-Position)
     * @param {number} [pixelX] - Pixel-X (proportionaler Modus)
     * @returns {number} Zeichen-Offset im sichtbaren Titel-Bereich
     */
    _separatorClickCharOffset(x, pixelX) {
        // Label sitzt direkt an Spalte labelCol (kein Spacer mehr) — analog zur
        // Render- und Cursor-Position-Logik in EditorView._renderSeparatorLine
        // und getCursorScreenPosition.
        const labelCol = 2
        if (this.view.renderer && this.view.renderer.isProportional() && pixelX !== undefined) {
            // Proportional: pixelX → Zeichenposition via Layout
            const renderer = this.view.renderer
            const charWidth = renderer.charWidth
            const titleStartPx = labelCol * charWidth
            const relPx = pixelX - titleStartPx
            if (relPx <= 0) return 0
            const para = this.model.getParagraph(this.model.cursor.paragraphId)
            if (!para || !para.text) return 0
            const text = para.text
            const formatRuns = [{ start: 0, end: text.length, bold: true, italic: false }]
            // _activeMetrics liefert System-Font-Cache (z.B. NotoSans bei Garamond)
            const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
            const layout = computeStringLayout(text, activeCache, activeCache.baseStep, formatRuns)
            const scaledRelPx = relPx / activeSF
            // Nächste Zeichenposition finden
            const codepoints = Array.from(text)
            for (let i = 0; i < codepoints.length; i++) {
                const charMid = layout.positions[i] + layout.widths[i] / 2
                if (scaledRelPx < charMid) return i
            }
            return codepoints.length
        }
        // Monospace: einfacher Grid-Offset
        return Math.max(0, x - labelCol)
    }

    /**
     * Delegator: berechnet Edit-Pane fuer Arrow-Nav auf Separator.
     * @param {'text'|'note'|'info'} previousField
     * @returns {'main'|'outline'|'context'}
     */
    _separatorEditPaneForField(previousField) {
        return this._separatorBridge.editPaneForField(previousField)
    }

    /**
     * Delegator: Arrow-Keys auf Separator. Intern genutzt vom SingleLineInput-
     * Callback, aber auch von Tests direkt angesprochen — daher oeffentlich
     * als Delegator erhalten.
     * @param {'up'|'down'|'left'|'right'} direction
     */
    _onSeparatorNavigateOut(direction) {
        this._separatorBridge.onNavigateOut(direction)
    }

    _handleSeparatorRemoveBackspace() {
        if (!this.model.cursor) return
        const paraIndex = this.model.getParagraphIndex(this.model.cursor.paragraphId)
        if (paraIndex <= 0) return
        this._confirmSeparatorRemove(paraIndex - 1)
    }

    /**
     * Behandelt Delete an Separator: Der Separator steht NACH dem aktuellen Absatz.
     */
    _handleSeparatorRemoveDelete() {
        if (!this.model.cursor) return
        const paraIndex = this.model.getParagraphIndex(this.model.cursor.paragraphId)
        if (paraIndex < 0 || paraIndex >= this.model.paragraphs.length - 1) return
        this._confirmSeparatorRemove(paraIndex + 1)
    }

    /**
     * Begrenzt die Selection auf die Grenzen des aktuellen Abschnitts.
     * Aufgerufen nach jeder Shift+Arrow-Navigation.
     */
    _clampSelectionToSection() {
        if (!this.model.selection || !this.model.cursor) return
        if (this.model.cursor.field !== 'text') return

        const { startIndex, endIndex } = this.model.getSectionBounds(this.model.selection.anchor.paragraphId)
        const cursorIndex = this.model.getParagraphIndex(this.model.cursor.paragraphId)

        if (cursorIndex < startIndex) {
            const firstPara = this.model.paragraphs[startIndex]
            this.model.cursor.paragraphId = firstPara.id
            this.model.cursor.offset = 0
            this.model.selection.cursor = this.model.cursor.clone()
        } else if (cursorIndex > endIndex) {
            const lastPara = this.model.paragraphs[endIndex]
            this.model.cursor.paragraphId = lastPara.id
            this.model.cursor.offset = lastPara.text.length
            this.model.selection.cursor = this.model.cursor.clone()
        }
    }

    /**
     * Prüft ob der Cursor an einer Wortgrenze steht (mindestens eine Seite ist kein Wort-Zeichen).
     * Textanfang und Textende gelten als Wortgrenze.
     */
    _isAtWordBoundary() {
        if (!this.model.cursor) return false
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return false
        const field = this.model.cursor.field || 'text'
        const text = this.model._getFieldText(para, field)
        const offset = this.model.cursor.offset

        const charBefore = offset > 0 ? text[offset - 1] : null
        const charAfter = offset < text.length ? text[offset] : null
        // Wortgrenze = mindestens eine Seite ist kein Wort-Zeichen (oder Textrand)
        return !charBefore || !charAfter || !isWordChar(charBefore) || !isWordChar(charAfter)
    }

    /**
     * Stellt sicher, dass nach Deletion/Paste keine Wörter zusammenkleben
     * und keine Doppel-Spaces entstehen.
     * Manipuliert den Text direkt (ohne Model-Methoden), damit kein extra Undo-Eintrag entsteht.
     * Der Aufrufer muss den Undo-Stack selbst zusammenfassen (undoStackSize-Pattern).
     *
     * @param {number|null} insertStartOffset - Linke Grenze bei Paste (wo eingefügter Text beginnt)
     */
    _ensureSmartBlanks(insertStartOffset) {
        if (!this.model.cursor) return
        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return
        const field = this.model.cursor.field || 'text'
        // Code-Block-Paragraphs sind raw — Mehrfach-Leerstellen sind erlaubt
        // und semantisch wichtig (Indentation). Smart-Blanks würden sie schlucken.
        if (para.codeBlock && field === 'text') return
        const text = this.model._getFieldText(para, field)
        const result = tpEnsureSmartBlanks(text, this.model.cursor.offset,
            insertStartOffset != null ? insertStartOffset : null)
        if (result.newText !== text) {
            this.model._setFieldText(para, field, result.newText)
            this.model.cursor.offset = result.newCursorOffset

            // Frische Referenz holen (_setFieldText kann COW-Klon erzeugen)
            const freshPara = this.model.getParagraph(para.id)

            // RT-Modus: Spans/Links für jede Insert/Delete-Op anpassen
            if (freshPara.type !== 'separator' && result.ops && result.ops.length > 0) {
                let formats = freshPara.formats[field]
                let links = freshPara.links[field]
                for (const op of result.ops) {
                    if (op.type === 'insert') {
                        formats = adjustSpansForInsert(formats, op.offset, 1)
                        links = adjustLinksForInsert(links, op.offset, 1)
                    } else if (op.type === 'delete') {
                        formats = adjustSpansForDelete(formats, op.offset)
                        links = adjustLinksForDelete(links, op.offset)
                    }
                }
                freshPara.formats[field] = formats
                freshPara.links[field] = links
            }
            this.model._markParagraphDirty(freshPara.id)
        }
    }

    /**
     * Erstellt einen Snapshot des aktuellen Model-States für atomare Undo-Zusammenfassung.
     */
    _takeSnapshot() {
        this.model._gen++
        return {
            paragraphs: this.model.paragraphs.slice(),
            cursor: this.model.cursor ? this.model.cursor.clone() : null,
            selection: this.model.selection ? this.model.selection.clone() : null
        }
    }

    /**
     * Fasst alle seit undoStackSize neu hinzugekommenen Undo-Einträge zu einem zusammen.
     * Der zusammengefasste Eintrag stellt den Snapshot (paragraphs, cursor, selection) wieder her.
     */
    _collapseUndoEntries(undoStackSize, snapshot) {
        // Alle neuen Einträge entfernen
        this.model.undoStack.length = undoStackSize
        // Einen kombinierten Eintrag hinzufügen
        this.model.undoStack.push(() => {
            this.model.paragraphs = snapshot.paragraphs
            this.model.cursor = snapshot.cursor
            this.model.selection = snapshot.selection
            this.model._markStructuralDirty()
        })
        this.model.redoStack = []
    }

    /**
     * Zählt die berührten Wörter zwischen zwei Positionen.
     * Nutzt Raw-Text (Marker-Zeichen sind keine Wort-Zeichen, also kein Verfälschungs-Risiko).
     *
     * @param {Position} fromPos
     * @param {Position} toPos
     * @returns {number}
     */
    _countTouchedWords(fromPos, toPos) {
        const model = this.model
        const fromIdx = model.getParagraphIndex(fromPos.paragraphId)
        const toIdx = model.getParagraphIndex(toPos.paragraphId)

        // Normalisieren: start vor end
        let startPos, endPos, startIdx, endIdx
        if (fromIdx < toIdx || (fromIdx === toIdx && fromPos.offset <= toPos.offset)) {
            startPos = fromPos; endPos = toPos; startIdx = fromIdx; endIdx = toIdx
        } else {
            startPos = toPos; endPos = fromPos; startIdx = toIdx; endIdx = fromIdx
        }


        let wordCount = 0

        for (let i = startIdx; i <= endIdx; i++) {
            const para = model.paragraphs[i]
            const text = para.text
            const rangeStart = (i === startIdx) ? startPos.offset : 0
            const rangeEnd = (i === endIdx) ? endPos.offset : text.length

            // Wörter im Range zählen
            let inWord = false
            for (let j = rangeStart; j < rangeEnd; j++) {
                if (isWordChar(text[j])) {
                    if (!inWord) { wordCount++; inWord = true }
                } else {
                    inWord = false
                }
            }

            // Wenn rangeStart mitten in einem Wort liegt, wurde es schon gezählt
            // Aber wenn rangeStart auf einem Wortzeichen liegt UND das Zeichen davor
            // auch ein Wortzeichen ist, ist das Wort schon „berührt" aber evtl. nicht gezählt
            // → wird durch die Schleife korrekt behandelt
        }

        return wordCount
    }

    // ===== Temporäre Aktivierung =====

    /**
     * Aktiviert einen passiven Absatz temporär für Note-Eingabe.
     * Speichert den Original-Zustand für Revert.
     * @param {Paragraph} para - Der Absatz
     */
    _activateTemporarily(para) {
        // Background-Separator darf nie aktiviert werden
        if (para.type === 'separator' && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return
        // Outline-Redesign Phase 3b: Outline-Items brauchen kein Pending —
        // Cursor landet ohnehin direkt in der Note.
        if (para.isOutlineItem()) return

        // Outline-Redesign Phase 3b: kein Mutate mehr — stattdessen Pending-
        // Cursor setzen. Der neue Outline-Item entsteht beim ersten Edit-
        // Tastendruck (charKey, Tab, Enter) via _materializePendingForEdit().
        // Default-Tiefe: gleiche Tiefe wie das umschließende Outline-Item
        // (Geschwister, kein Kind). Normal-Paras tragen selbst keine Tiefe
        // (immer 0), daher rückwärts bis zum ersten Outline-Item suchen; ein
        // Separator oder der Doc-Anfang setzt die Baseline auf 0.
        const paraIndex = this.model.getParagraphIndex(para.id)
        const predecessor = paraIndex > 0 ? this.model.paragraphs[paraIndex - 1] : null
        const insertAfterParagraphId = predecessor ? predecessor.id : null
        let ownerDepth = 0
        for (let j = paraIndex - 1; j >= 0; j--) {
            const p = this.model.paragraphs[j]
            if (p.type === 'separator') break
            if (p.isOutlineItem()) { ownerDepth = p.outlineDepth; break }
        }
        const depth = Math.max(0, Math.min(10, ownerDepth))

        this.model.setPendingCursor({ insertAfterParagraphId, depth })

        // Cursor visuell auf die Phantom-Note-Zeile dieses Paras. Der
        // Renderer zeichnet den Cursor an dieser Position (unveraendert
        // gegenueber dem alten Temp-Aktivierungs-Verhalten); bei
        // Materialisierung wandert der Cursor auf das neue Outline-Item.
        this.model.cursor = new Position(para.id, 0, 'note')

        this._tempActivation = { paraId: para.id }
        this.model._markStructuralDirty()
    }

    /**
     * Outline-Redesign Phase 3b: Pending-Cursor materialisieren, bevor ein
     * Edit-Tastendruck ausgefuehrt wird. Aufrufer sind die Char-Eingabe,
     * Tab/Shift-Tab und Enter-Pfade. Auto-Para-Cleanup macht der zentrale
     * Hook in _checkParagraphChange via _autoCreatedParagraphIds — der
     * Helper braucht hier kein lokales Bookkeeping.
     * @returns {boolean} true wenn ein Outline-Item materialisiert wurde
     */
    _materializePendingForEdit() {
        if (!this.model.hasPendingCursor()) return false
        const res = this.model.materializePendingCursor()
        return res.ok === true
    }

    /**
     * Phase-3b-Variante des frueheren Temp-Aktivierungs-Reverts: raeumt nur
     * den Pending-Cursor ab (kein Auto-Para zu cleanen — der zentrale Hook
     * in _checkParagraphChange macht das, sobald der Auto-Para verlassen
     * wird, basierend auf _autoCreatedParagraphIds).
     */
    _checkTempActivationRevert() {
        if (!this._tempActivation) return
        this.model.clearPendingCursor()
        this._tempActivation = null
    }

    /**
     * Raeumt Ressourcen auf (Timer stoppen).
     */
    dispose() {
        this._stopAutoScroll()
    }
}
